import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { trackPageView, track, trackKlaviyo } from '@/lib/analytics';
import {
  LayoutDashboard,
  Layers,
  LogOut,
  Loader2,
  ArrowLeft,
  Wand2,
  Sparkles,
  Settings,
  Coins,
  Shield,
  Gift,
  LayoutGrid,
  ExternalLink,
  HelpCircle,
} from 'lucide-react';
import { saveAs } from 'file-saver';

import { useAuth } from '@/context/AuthContext';
import { 
  Sidebar, 
  SidebarBody,
  SidebarLinkItem,
  SidebarLogo,
  SidebarUserItem,
  Lightbox,
  RegenerationModal,
  type SidebarLink
} from '@/components/ui';
import {
  DashboardView,
  ProjectDetailsView,
  SetupView,
  type PropertyDetails,
  UploadView,
  ReviewView,
  UpscaleSelectionView,
  BrandingView,
  ForgotPasswordView,
  LoginView,
  UpdatePasswordView,
  OverlayToolView,
  OverlayBuilderView,
  SettingsView,
  CreditsView,
  AffiliateDashboardView,
  FloorplanUploadView,
  FloorplanUnitSelectionView,
  FloorplanLabelsView,
  FloorplanEditorView,
  FloorplanReviewView,
  FloorplanThreeDView,
  FloorplanBrandingView,
  AdminRoute,
  AdminDashboard,
  OnboardingView,
  HelpView,
} from '@/components/views';
import { ProjectWizardView } from '@/components/views/ProjectWizardView';
import { ProjectWorkspaceView } from '@/components/views/ProjectWorkspaceView';
import { TenantUploadView } from '@/views/public/TenantUploadView';
import { HelpButton } from '@/components/HelpButton';
import { WizardResumeBubble } from '@/components/WizardResumeBubble';
import { FloorplanResumeBubble } from '@/components/FloorplanResumeBubble';
import {
  saveFloorplanSession,
  loadFloorplanSession,
  clearFloorplanSession,
  type FloorplanFlowView,
} from '@/lib/floorplanSession';
import { NewProjectModal, type NewProjectTool } from '@/components/NewProjectModal';
import { LandingPage } from '@/components/landing';
import { DashboardPreview } from '@/views/dev/DashboardPreview';
import { TryoutView } from '@/views/public/TryoutView';
import { TryoutListDetailView } from '@/components/views/admin/TryoutListDetailView';
import { BlogRouter } from '@/views/public/BlogRouter';
import { DatenschutzView } from '@/views/public/DatenschutzView';
import { ImpressumView } from '@/views/public/ImpressumView';
import { fetchCreditState, subscribeToCreditState, ensureProfileExists, fetchUserProfile, CreditState } from '@/services/paymentService';
import { generateRoomImage, requestStageRefinement, requestStageRefinementPreview, analyzeCatalogStaging } from '@/services/geminiService';
import { upscaleImageWithPolling } from '@/services/upscaleService';
import { applyBrandingDetailed, BrandingLayerError, renderBrandedExport, uploadBrandedImagesToSupabase, compressForExport } from '@/services/imageProcessing';
import { applyAiLabel, buildExportZip, exportZipFileName, type ExportEntry } from '@/services/exportService';
import { useAiLabelPrefs } from '@/components/ui/AiLabelOption';
import { convertHeicToJpeg } from '@/services/heicConversion';
import {
  fetchProjects,
  createProject,
  updateProject,
  uploadProjectImage,
  getPublicUrl,
  deleteProject,
  updateImageMetadata,
  deleteProjectImage,
  fetchUnprocessedImagesForStaging,
  saveHeroOverlayConfig,
  saveHeroImagePath,
  clearOtherHeroImages,
  fetchProjectAssignments,
} from '@/services/projectService';
import {
  AppView,
  Project,
  UploadedImage,
  GenerationResult,
  GeneratedVersion,
  GenerationVariant,
  GenerationType,
  RoomType,
  BrandingConfig,
  AspectRatio,
  StagingStyle,
  UpscaleResult,
  HeroOverlayConfig,
  ProductUpdate,
  TutorialVideo,
  type BoxCoordinates,
} from '@/types';
import { getRoomLabel, supportsStaging } from '@/lib/roomConfig';
import { formatCredits } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { getAvatarUrl } from '@/services/avatarService';
import { useFloorplanWorkflow } from '@/hooks/useFloorplanWorkflow';
import { useAppSettings } from '@/hooks/useAppSettings';
import { TopBar } from '@/components/TopBar';
import { WhatsNewModal } from '@/components/WhatsNewModal';
import { TutorialPlayerModal } from '@/components/TutorialPlayerModal';
import { fetchPublishedUpdates, fetchReadUpdateIds, markUpdateRead } from '@/services/updatesService';
import { fetchPublishedTutorials, fetchTutorialReadIds, markTutorialRead } from '@/services/tutorialService';
import { selectAutoOpenUpdates, unreadCount as computeUnreadCount } from '@/lib/whatsNew';
import { selectSignupTutorials } from '@/lib/helpCenter';
import { effectiveUploadMode } from '@/lib/stagingFlow';
import type { SettingsTab } from '@/components/views/SettingsView';

// ============================================================================
// Constants
// ============================================================================

const CONCURRENCY_LIMIT = 2;

// Landing wieder aktiv: alle Immobilienfotos wurden am 07.09.2026 durch
// freigegebene Bilder ersetzt (Thomas' Freigabe zum Wieder-Livegang).
const LANDING_PAGE_ENABLED: boolean = true;

const DEFAULT_BRANDING: BrandingConfig = {
  logo: null,
  logoUrl: null,
  overlay: null,
  overlayUrl: null,
  logoPosition: 'bottom-right',
  // Watermark defaults
  watermarkUrl: null,
  watermarkOpacity: 30,
  watermarkPosition: 'center',
  watermarkScale: 0.5,
};

// ============================================================================
// URL Routing
// ============================================================================

const VIEW_TO_PATH: Record<AppView, string> = {
  'dashboard': '/app/dashboard',
  'project-details': '/app/project',
  'workspace': '/app/workspace',
  'wizard': '/app/wizard',
  'setup': '/app/setup',
  'upload': '/app/upload',
  'review': '/app/review',
  'upscale': '/app/upscale',
  'branding': '/app/branding',
  'overlay-tool': '/app/overlay-tool',
  'overlay-builder': '/app/overlay-builder',
  'settings': '/app/settings',
  'credits': '/app/credits',
  'admin': '/app/admin',
  'affiliate': '/app/affiliate',
  'help': '/app/hilfe',
  'floorplan-upload': '/app/floorplan/upload',
  'floorplan-unit-selection': '/app/floorplan/units',
  'floorplan-labels': '/app/floorplan/labels',
  'floorplan-editor': '/app/floorplan/editor',
  'floorplan-review': '/app/floorplan/review',
  'floorplan-3d': '/app/floorplan/3d',
  'floorplan-branding': '/app/floorplan/branding',
};

// Views whose header lives in the TopBar. Others show the wordmark and keep their own header.
const VIEW_META: Partial<Record<AppView, { title: string; subtitle: string }>> = {
  dashboard: { title: 'Projekte', subtitle: 'Verwalte deine Immobilien-Staging Projekte.' },
  credits: { title: 'Abonnement & Guthaben', subtitle: 'Verwalte dein Abo, buche das Grundriss-Paket dazu und lade bei Bedarf Credits nach.' },
  help: { title: 'Hilfe & Tutorials', subtitle: 'Kurze Videos zu allen Funktionen.' },
};

const PATH_TO_VIEW = new Map(
  Object.entries(VIEW_TO_PATH).map(([view, path]) => [path, view as AppView])
);

function pathToView(pathname: string): AppView | null {
  if (pathname === '/app' || pathname === '/app/') return 'dashboard';
  return PATH_TO_VIEW.get(pathname) ?? null;
}

// Views that operate on a single project and therefore carry ?p=<id> in the URL.
// Everything NOT listed here (dashboard, credits, settings, admin, affiliate) is
// account-level: it must never get a ?p= appended, and a stale ?p= on such a URL
// must not be interpreted as a project to rehydrate. Keeping this list explicit is
// what makes a reload of e.g. /app/credits?p=<id> stay on Credits instead of
// falling through to the staging Upload view.
const PROJECT_SCOPED_VIEWS = new Set<AppView>([
  'project-details', 'workspace', 'wizard',
  'setup', 'upload', 'review', 'upscale', 'branding',
  'overlay-tool', 'overlay-builder',
  'floorplan-upload', 'floorplan-unit-selection', 'floorplan-labels',
  'floorplan-editor', 'floorplan-review', 'floorplan-3d', 'floorplan-branding',
]);

// Staging sub-flow views (all rehydrate the same uploaded-image set from the DB).
const STAGING_VIEWS = new Set<AppView>(['upload', 'review', 'upscale', 'branding']);

const FLOORPLAN_VIEWS = new Set<AppView>([
  'floorplan-upload', 'floorplan-unit-selection', 'floorplan-labels',
  'floorplan-editor', 'floorplan-review', 'floorplan-3d', 'floorplan-branding',
]);

// ============================================================================
// App Component
// ============================================================================

// Auth View type for unauthenticated pages
type AuthView = 'login' | 'forgot-password' | 'update-password';

const App: React.FC = () => {
  // Dev-only dashboard preview (no auth, mock data) for design-token exploration
  if (import.meta.env.DEV && window.location.pathname === '/dev/dashboard') {
    return <DashboardPreview />;
  }

  const { user, isLoading: authLoading, signOut, isAdmin, isOnboarded, refreshOnboardingStatus, disabledFeatures } = useAuth();
  const { settings: appSettings } = useAppSettings();
  // Katalog-Staging v2: für Admins immer aktiv (Test ohne globales Ausrollen),
  // sonst nur wenn das Feature-Flag an ist.
  const catalogStagingActive = isAdmin ? appSettings.catalog_staging_admin : appSettings.catalog_staging_live;
  // Flow v3 (Empty-first Assistent + Workspace-Tabs, Migration 104): gleiche Admin/Live-Logik.
  const flowV3Active = isAdmin ? appSettings.flow_v3_admin : appSettings.flow_v3_live;
  const [wizardMode, setWizardMode] = useState<'new' | 'add'>('new');
  const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);
  // Wenn gesetzt, öffnet das Modal im „nur Name"-Modus für dieses Tool
  // (Sidebar-Unterpunkte „Neues Staging" / „Neuer Grundriss").
  const [newProjectLockedTool, setNewProjectLockedTool] = useState<NewProjectTool | null>(null);
  const [isCreatingNewProject, setIsCreatingNewProject] = useState(false);
  const tenantUploadToken = useMemo(() => {
    const match = window.location.pathname.match(/^\/upload\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  }, []);

  const tryoutToken = useMemo(() => {
    const match = window.location.pathname.match(/^\/tryout\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  }, []);

  const adminTryoutListId = useMemo(() => {
    const match = window.location.pathname.match(/^\/admin\/tryout-lists\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  }, []);

  const isBlogPath = useMemo(() => {
    const p = window.location.pathname;
    return p === '/blog' || p === '/blog/' || p.startsWith('/blog/');
  }, []);

  const isDatenschutzPath = useMemo(() => {
    const p = window.location.pathname;
    return p === '/datenschutz' || p === '/datenschutz/';
  }, []);

  const isImpressumPath = useMemo(() => {
    const p = window.location.pathname;
    return p === '/impressum' || p === '/impressum/';
  }, []);

  const [showBlog, setShowBlog] = useState(isBlogPath);
  const [showDatenschutz, setShowDatenschutz] = useState(isDatenschutzPath);
  const [showImpressum, setShowImpressum] = useState(isImpressumPath);

  // Auth View State (for unauthenticated pages)
  const [authView, setAuthView] = useState<AuthView>('login');

  // Sidebar State
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // View State — initialize from URL for deep-linking support
  const [currentView, setCurrentView] = useState<AppView>(
    () => pathToView(window.location.pathname) || 'dashboard'
  );
  const mainRef = useRef<HTMLElement>(null);
  const isPopStateRef = useRef(false);
  // Stable ref to projects list — used in popstate handler without re-registering the listener
  const projectsRef = useRef<Project[]>([]);
  // Wizard-Generierung aktiv? (für Navigations-Warnung; Refs halten navigateTo stabil)
  const [wizardBusy, setWizardBusy] = useState(false);
  const wizardBusyRef = useRef(false);
  const currentViewRef = useRef(currentView);
  useEffect(() => { wizardBusyRef.current = wizardBusy; }, [wizardBusy]);
  useEffect(() => { currentViewRef.current = currentView; }, [currentView]);
  // Ensures rehydration runs exactly once per login session
  const didRehydrateRef = useRef(false);

  // Redirect unknown /app/* paths to the safe default (dashboard if authed, /login otherwise).
  // Previously paths like /app/login fell through silently to the homepage, leaving a
  // stale URL in the address bar that didn't match the rendered content.
  useEffect(() => {
    const p = window.location.pathname;
    if (p.startsWith('/app/') && p !== '/app' && p !== '/app/' && !PATH_TO_VIEW.has(p)) {
      window.history.replaceState(null, '', '/');
    }
  }, []);

  // Scroll main content to top on view change + track page view
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    trackPageView(currentView);
  }, [currentView]);


  // Redirect authenticated users from landing/login to app URL
  useEffect(() => {
    if (!user) return;
    const path = window.location.pathname;
    if (path === '/' || path === '/login') {
      history.replaceState(null, '', VIEW_TO_PATH[currentView] || '/app/dashboard');
    }
  }, [user]);

  // Handle URL-based auth routing & clean up referrer param from URL
  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    
    // Clean up ref parameter from URL (already captured in main.tsx synchronously)
    if (urlParams.has('ref')) {
      urlParams.delete('ref');
      const newUrl = urlParams.toString() 
        ? `${path}?${urlParams.toString()}` 
        : path;
      window.history.replaceState({}, '', newUrl || '/');
    }
    
    if (path === '/forgot-password') {
      setAuthView('forgot-password');
    } else if (path === '/update-password') {
      setAuthView('update-password');
    } else {
      setAuthView('login');
    }
  }, []);

  // Robustheit: Falls der Reset-Link NICHT auf /update-password landet (z. B. weil
  // Supabase auf die Site-URL zurückfällt), fängt dieser globale Listener das
  // PASSWORD_RECOVERY-Event ab und zwingt die App auf die Set-Passwort-Seite —
  // unabhängig vom Landing-Pfad. Ohne das würde der Recovery-Auto-Login direkt
  // aufs Dashboard führen.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setAuthView('update-password');
        if (window.location.pathname !== '/update-password') {
          window.history.replaceState({}, '', '/update-password');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Navigate to auth view and update URL
  const navigateToAuthView = useCallback((view: AuthView) => {
    setAuthView(view);
    if (view === 'login') {
      window.history.pushState({}, '', '/');
    } else if (view === 'forgot-password') {
      window.history.pushState({}, '', '/forgot-password');
    }
  }, []);

  // Handle successful password update
  const handlePasswordUpdateSuccess = useCallback(() => {
    // Clear the URL and auth view - user is already logged in
    window.history.replaceState({}, '', '/');
    setAuthView('login');
    // User is already authenticated, app will now show dashboard
  }, []);

  // Project State
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectAssignments, setProjectAssignments] = useState<Record<string, string[]>>({});
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  // Form State
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectAddr, setNewProjectAddr] = useState('');

  // Workflow State
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [isProcessingBranding, setIsProcessingBranding] = useState(false);
  // Eine Quelle der Wahrheit für den KI-Hinweis: hier gehalten, weil der
  // ZIP-Handler hier lebt — die BrandingView bekommt sie als Prop.
  const aiLabel = useAiLabelPrefs();

  // Upscale State
  const [upscaleResults, setUpscaleResults] = useState<Map<string, UpscaleResult>>(new Map());
  const [isUpscaling, setIsUpscaling] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // UI State
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [activeRegen, setActiveRegen] = useState<{
    imgId: string;
    type: GenerationType;
    currentStoragePath?: string;
    currentUrl?: string;
    initialMode?: 'choose' | 'feedback';
  } | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  // Regenerate-suggestion advisory (perspective etc.) surfaced by the refinement.
  const [regenSuggestion, setRegenSuggestion] = useState<{ reason: string | null } | null>(null);
  // Background generation: while a refinement runs, the modal is hidden (kept
  // mounted so its feedback survives) and the tile's own spinner shows progress
  // — the screen stays interactive. Re-shown for the advisory / an error.
  const [bgGenerating, setBgGenerating] = useState(false);
  // Ref mirror of activeRegen so async completion handlers can tell whether the
  // modal is still "about" the tile they were generating for (concurrent edits).
  const activeRegenRef = useRef(activeRegen);
  activeRegenRef.current = activeRegen;

  // Credits State (Pricing v4: Töpfe — Monats-Credits, Top-ups, Grundriss-Kontingent)
  const [creditState, setCreditState] = useState<CreditState | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  // Summe der Bild-Credits (Monats + Top-up) — von allen bestehenden Views genutzt
  const credits = creditState?.totalImageCredits ?? 0;
  const floorplanUnits = creditState?.totalFloorplanUnits ?? 0;

  const refreshCredits = useCallback(async () => {
    if (!user?.id) return;
    try {
      const state = await fetchCreditState(user.id);
      setCreditState(state);
    } catch (e) {
      console.error('Error refreshing credit state:', e);
    }
  }, [user?.id]);

  // True while restoring project + images from a URL reload; prevents the upload view flashing empty
  const [isRehydrating, setIsRehydrating] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const view = pathToView(window.location.pathname);
    return !!params.get('p') && !!view && PROJECT_SCOPED_VIEWS.has(view);
  });

  // Team creator map (for admin to see who created which project)
  const [creatorMap, setCreatorMap] = useState<Record<string, { name: string; email: string; avatarPath: string | null }>>({});
  // Team members list (for dashboard filter + project assignment)
  const [teamMembersList, setTeamMembersList] = useState<Array<{ id: string; name: string; email: string; avatarPath: string | null }>>([]);
  // User avatar URL for sidebar
  const [userAvatarUrl, setUserAvatarUrl] = useState<string | null>(null);

  const hasTeam = teamMembersList.length > 0;
  const [updates, setUpdates] = useState<ProductUpdate[]>([]);
  const [readUpdateIds, setReadUpdateIds] = useState<Set<string>>(new Set());
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewItems, setWhatsNewItems] = useState<ProductUpdate[]>([]);
  const [tutorials, setTutorials] = useState<TutorialVideo[]>([]);
  const [tutorialReadIds, setTutorialReadIds] = useState<Set<string>>(new Set());
  // Nach Onboarding-Abschluss einmalig die Signup-Tutorials zeigen.
  // Flag statt Direktöffnung: die Tutorials sind zu diesem Zeitpunkt evtl. noch nicht geladen.
  const [pendingSignupTutorials, setPendingSignupTutorials] = useState(false);
  const [signupTutorials, setSignupTutorials] = useState<TutorialVideo[]>([]);
  const [signupTutorialsOpen, setSignupTutorialsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<SettingsTab | undefined>(undefined);
  const autoOpenedRef = useRef(false);

  // Download Preference State
  const [defaultDownloadCompressed, setDefaultDownloadCompressed] = useState(true);

  // Default Branding Asset IDs (loaded from profile)
  const [defaultBrandingAssetIds, setDefaultBrandingAssetIds] = useState<{
    logoAssetId: string | null;
    overlayAssetId: string | null;
    watermarkAssetId: string | null;
  } | null>(null);

  // Floor Plan Workflow
  const {
    floorplanState,
    updateFloorplanState,
    resetFloorplanState,
    loadExistingFloorplan,
    handleGenerate2D,
    handleUnitExtraction,
    handleGenerate3D,
    handleSave2DTransform,
    handleSave3DTransform,
    handleSaveArrow2D,
    handleSaveArrow3D,
    handleSaveComposite,
    handleStartOverFloorplan,
  } = useFloorplanWorkflow(currentProject, user);

  // ============================================================================
  // Effects
  // ============================================================================

  // Keep projectsRef in sync so the popstate handler always sees the current list
  useEffect(() => { projectsRef.current = projects; }, [projects]);

  // Sync URL when currentView or currentProject changes — includes ?p=PROJECT_ID for project-scoped views
  useEffect(() => {
    if (!user) return;
    if (isRehydrating) return; // project not restored yet — don't strip ?p= from the URL
    if (isPopStateRef.current) {
      isPopStateRef.current = false;
      return;
    }
    // Don't fight URLs handled by early-return branches (admin tryout list detail,
    // public tryout funnel). currentView is meaningless for those paths.
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/admin/tryout-lists/')) return;
    if (currentPath.startsWith('/tryout/')) return;
    const path = VIEW_TO_PATH[currentView];
    if (!path) return;
    const params = new URLSearchParams();
    // Only project-scoped views carry ?p=. Account-level views (credits, settings,
    // admin, affiliate) must stay clean so a reload restores the same view.
    if (currentProject && PROJECT_SCOPED_VIEWS.has(currentView)) params.set('p', currentProject.id);
    // Rücksprung-Ziel (Overlay-Builder-Flow) und Tab-Deep-Link (?tab=export) nicht wegschreiben
    const existing = new URLSearchParams(window.location.search);
    const returnTo = existing.get('return');
    if (returnTo && currentView === 'overlay-builder') params.set('return', returnTo);
    const tab = existing.get('tab');
    if (tab && currentView === 'workspace') params.set('tab', tab);
    const search = params.toString() ? `?${params.toString()}` : '';
    const fullUrl = `${path}${search}`;
    if (currentPath + window.location.search !== fullUrl) {
      history.pushState({ view: currentView, projectId: currentProject?.id ?? null }, '', fullUrl);
    }
  }, [currentView, currentProject, user, isRehydrating]);

  // Flow v3 replaces the standalone Setup step (room-type/aspect chooser) with the
  // project wizard + workspace tabs, so /app/setup must be inaccessible for those
  // users. Bounce any landing on 'setup' to the project hub (workspace, if a project
  // is loaded) or the dashboard. replaceState keeps Setup out of the history stack.
  // Guarded by isRehydrating so it doesn't fire mid-restore (rehydration maps a
  // setup URL to the staging Upload view on its own).
  useEffect(() => {
    if (isRehydrating || !flowV3Active || currentView !== 'setup') return;
    const target: AppView = currentProject ? 'workspace' : 'dashboard';
    isPopStateRef.current = true; // suppress the URL-sync pushState for this bounce
    setCurrentView(target);
    const search = target === 'workspace' && currentProject ? `?p=${currentProject.id}` : '';
    window.history.replaceState(null, '', `${VIEW_TO_PATH[target]}${search}`);
  }, [flowV3Active, currentView, currentProject, isRehydrating]);

  // Restore project + images from URL — runs once after the initial projects load
  const rehydrateFromUrl = useCallback(async (loadedProjects: Project[]) => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('p');
    const view = pathToView(window.location.pathname);

    // Nothing to restore when there's no ?p=, no resolvable view, or the view is
    // account-level (a stale ?p= on /credits, /settings, …). Keep the URL's view.
    if (!projectId || !view || !PROJECT_SCOPED_VIEWS.has(view)) {
      setIsRehydrating(false);
      return;
    }

    const project = loadedProjects.find(p => p.id === projectId);
    if (!project) { setIsRehydrating(false); return; }

    setCurrentProject(project);

    if (view === 'wizard') {
      // Assistent nach Reload im 'add'-Modus fortsetzen (Projekt existiert bereits)
      setWizardMode('add');
      setCurrentView('wizard');
      setIsRehydrating(false);
      return;
    }

    if (FLOORPLAN_VIEWS.has(view)) {
      setCurrentView(view);
      try {
        await loadExistingFloorplan(projectId);
      } catch (err) {
        console.error('[Rehydration] Failed to load floorplan state:', err);
      } finally {
        setIsRehydrating(false);
      }
      return;
    }

    // Views that just need the project reference (project hub, overlay tools, exposé):
    // the URL view is already valid — restore the project and keep the view.
    if (!STAGING_VIEWS.has(view) && view !== 'setup') {
      setCurrentView(view);
      setIsRehydrating(false);
      return;
    }

    // Staging sub-flow (setup / upload / review / upscale / branding) — respect the
    // URL view; setup has no standalone reload target, so fall back to upload.
    setResults([]);
    setCurrentView(STAGING_VIEWS.has(view) ? view : 'upload');
    try {
      const existingImages = await fetchUnprocessedImagesForStaging(projectId);
      const uploadedFromDb: UploadedImage[] = existingImages.map(img => ({
        id: img.id,
        previewUrl: img.previewUrl,
        roomType: img.roomType as RoomType,
        customLabel: img.customLabel,
        keepKitchen: img.keepKitchen,
        dbId: img.dbId,
        storagePath: img.storagePath,
        isFromDatabase: true,
        isTenantUpload: img.isTenantUpload,
      }));
      setUploadedImages(uploadedFromDb);
      console.log(`[Rehydration] Restored ${existingImages.length} images for project ${projectId}`);
    } catch (err) {
      console.error('[Rehydration] Failed to load images:', err);
      setUploadedImages([]);
    } finally {
      setIsRehydrating(false);
    }
  }, [loadExistingFloorplan]);

  // Handle browser back/forward — restores project from ?p= without re-fetching images
  useEffect(() => {
    const handlePopState = () => {
      const view = pathToView(window.location.pathname);
      if (view) {
        isPopStateRef.current = true;
        setCurrentView(view);
      }
      const params = new URLSearchParams(window.location.search);
      const projectId = params.get('p');
      if (projectId) {
        const project = projectsRef.current.find(p => p.id === projectId);
        if (project) setCurrentProject(project);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Load and subscribe to the credit state (buckets; Agency teams share the admin's pool)
  useEffect(() => {
    if (!user) {
      setCreditState(null);
      setIsLoadingCredits(false);
      return;
    }

    let unsubscribe: (() => void) | undefined;
    let isSubscribed = true;

    const loadCredits = async () => {
      setIsLoadingCredits(true);

      // Ensure profile exists (for users who signed up before credit system)
      await ensureProfileExists(user.id, user.email || undefined);

      if (!isSubscribed) return;

      const state = await fetchCreditState(user.id);
      if (isSubscribed) {
        setCreditState(state);
        setIsLoadingCredits(false);
      }

      // Subscribe to real-time changes on the credit owner's profile row
      unsubscribe = subscribeToCreditState(user.id, state.ownerId, (newState) => {
        if (isSubscribed) {
          setCreditState(newState);
        }
      });
    };

    loadCredits();

    return () => {
      isSubscribed = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  // Load team members for creator map (admin sees who created which project)
  useEffect(() => {
    if (!user) return;
    const loadCreatorMap = async () => {
      const { data } = await supabase.rpc('get_team_members');
      if (data) {
        const map: Record<string, { name: string; email: string; avatarPath: string | null }> = {};
        const members: Array<{ id: string; name: string; email: string; avatarPath: string | null }> = [];
        for (const m of data) {
          if (m.member_id) {
            const avatarPath = m.avatar_path ?? null;
            map[m.member_id] = { name: m.member_name || '', email: m.email, avatarPath };
            members.push({ id: m.member_id, name: m.member_name || '', email: m.email, avatarPath });
          }
        }
        setCreatorMap(map);
        setTeamMembersList(members);
      }
    };
    loadCreatorMap();
  }, [user]);

  // Load user download preference and branding defaults
  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      const profile = await fetchUserProfile(user.id);
      if (profile) {
        setDefaultDownloadCompressed(profile.defaultDownloadCompressed);
        // Set avatar URL for sidebar
        setUserAvatarUrl(profile.avatarPath ? getAvatarUrl(profile.avatarPath) : null);
        // Apply branding parameter defaults
        setBranding(prev => ({
          ...prev,
          logoPosition: profile.defaultLogoPosition,
          watermarkOpacity: profile.defaultWatermarkOpacity,
          watermarkScale: profile.defaultWatermarkScale,
          watermarkPosition: profile.defaultWatermarkPosition,
        }));
        // Store asset IDs for BrandingView to resolve
        setDefaultBrandingAssetIds({
          logoAssetId: profile.defaultLogoAssetId,
          overlayAssetId: profile.defaultOverlayAssetId,
          watermarkAssetId: profile.defaultWatermarkAssetId,
        });
      }
    };
    loadProfile();
  }, [user]);

  // Load projects when user changes; triggers URL rehydration on first load
  useEffect(() => {
    const loadProjects = async () => {
      if (!user) {
        setProjects([]);
        didRehydrateRef.current = false;
        return;
      }

      setIsLoadingProjects(true);
      try {
        const [data, assignments] = await Promise.all([
          fetchProjects(user.id),
          fetchProjectAssignments(),
        ]);
        setProjects(data);
        setProjectAssignments(assignments);

        if (!didRehydrateRef.current) {
          didRehydrateRef.current = true;
          await rehydrateFromUrl(data);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Projekte:', error);
        setIsRehydrating(false);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadProjects();
  }, [user, rehydrateFromUrl]);

  // ============================================================================
  // Navigation
  // ============================================================================

  const navigateTo = useCallback((view: AppView) => {
    // Warnen, wenn im Assistenten gerade eine Generierung läuft (Sidebar-Nav).
    if (currentViewRef.current === 'wizard' && view !== 'wizard' && wizardBusyRef.current) {
      if (!window.confirm('Im Assistenten läuft gerade eine Generierung. Wenn du jetzt wegnavigierst, siehst du das Ergebnis erst später im Projekt. Trotzdem verlassen?')) {
        return;
      }
    }
    setCurrentView(view);
    if (view === 'dashboard') {
      setCurrentProject(null);
      setUploadedImages([]);
      setResults([]);
      setUpscaleResults(new Map());
      setIsUpscaling(false);
      resetFloorplanState();
    }
  }, [resetFloorplanState]);

  const goBackToUpload = useCallback(() => {
    setCurrentView('upload');
  }, []);

  const goBackToReview = useCallback(() => {
    setCurrentView('review');
  }, []);

  const goBackToUpscale = useCallback(() => {
    setCurrentView('upscale');
  }, []);

  // Zurück zum Projekt-Hub: Flow v3 → Workspace-Tabs, sonst klassische Detailseite.
  const goBackToProjectDetails = useCallback(() => {
    setCurrentView(flowV3Active ? 'workspace' : 'project-details');
  }, [flowV3Active]);

  // ============================================================================
  // Auth Actions
  // ============================================================================

  const handleSignOut = useCallback(async () => {
    await signOut();
    setCurrentView('dashboard');
    setCurrentProject(null);
    setUploadedImages([]);
    setResults([]);
    history.replaceState(null, '', '/');
  }, [signOut]);

  // Load published updates + per-user read state once the user is known.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([fetchPublishedUpdates(), fetchReadUpdateIds()])
      .then(([ups, read]) => { if (!cancelled) { setUpdates(ups); setReadUpdateIds(new Set(read)); } })
      .catch((err) => console.error('[Updates] load', err));
    return () => { cancelled = true; };
  }, [user]);

  // Tutorials + Read-State laden, sobald der User bekannt ist
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([fetchPublishedTutorials(), fetchTutorialReadIds()])
      .then(([vids, read]) => { if (!cancelled) { setTutorials(vids); setTutorialReadIds(new Set(read)); } })
      .catch((err) => console.error('[Tutorials] load', err));
    return () => { cancelled = true; };
  }, [user]);

  const handleTutorialViewed = useCallback((video: TutorialVideo) => {
    track('Tutorial Video Opened', {
      video_id: video.id,
      video_title: video.title,
      source: currentView === 'help' ? 'help_view' : 'signup_popup',
    });
    setTutorialReadIds((prev) => {
      if (prev.has(video.id)) return prev;
      const next = new Set(prev); next.add(video.id); return next;
    });
    void markTutorialRead(video.id);
  }, [currentView]);

  const handleOnboardingComplete = useCallback(() => {
    refreshOnboardingStatus();
    setPendingSignupTutorials(true);
  }, [refreshOnboardingStatus]);

  // Öffnet das Signup-Tutorial-Popup, sobald nach dem Onboarding die Tutorials geladen sind.
  useEffect(() => {
    if (!pendingSignupTutorials || tutorials.length === 0) return;
    const unseen = selectSignupTutorials(tutorials, tutorialReadIds);
    if (unseen.length > 0) {
      setSignupTutorials(unseen); // Snapshot — markRead darf die laufende Anzeige nicht schrumpfen
      setSignupTutorialsOpen(true);
    }
    setPendingSignupTutorials(false);
  }, [pendingSignupTutorials, tutorials, tutorialReadIds]);

  // Auto-open the What's-New modal once per app load if recent unread updates exist.
  // Deferred while onboarding is incomplete or the signup-tutorials popup is showing:
  // the effect re-runs when signupTutorialsOpen flips back to false (autoOpenedRef is
  // still unset), so What's-New opens AFTER the tutorial popup closes instead of stacking.
  useEffect(() => {
    if (autoOpenedRef.current || !isOnboarded || signupTutorialsOpen || updates.length === 0) return;
    const auto = selectAutoOpenUpdates(updates, readUpdateIds, Date.now());
    if (auto.length > 0) {
      autoOpenedRef.current = true;
      setWhatsNewItems(auto);       // snapshot so marking-read doesn't shrink the shown list
      setWhatsNewOpen(true);
    }
  }, [updates, readUpdateIds, isOnboarded, signupTutorialsOpen]);

  // Reset the deep-link target when leaving Settings so a later plain
  // navigate('settings') (e.g. the sidebar entry) remounts on 'profile',
  // not the last account-menu tab. navigateToSettings sets the tab THEN
  // navigates, so this effect (currentView !== 'settings') never wipes it
  // before SettingsView mounts.
  useEffect(() => {
    if (currentView !== 'settings' && settingsInitialTab !== undefined) setSettingsInitialTab(undefined);
  }, [currentView, settingsInitialTab]);

  const handleUpdateRead = useCallback((id: string) => {
    setReadUpdateIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev); next.add(id); return next;
    });
    void markUpdateRead(id);
  }, []);

  const navigateToSettings = useCallback((tab: SettingsTab) => {
    setSettingsInitialTab(tab);
    navigateTo('settings');
  }, [navigateTo]);

  const openWhatsNewManual = useCallback(() => {
    setWhatsNewItems(updates);      // manual entry shows full published history
    setWhatsNewOpen(true);
  }, [updates]);

  // ============================================================================
  // Project Actions
  // ============================================================================

  const handleCreateProject = useCallback(async (aspectRatio: AspectRatio = '4:3', stagingStyle: StagingStyle = 'modern', propertyDetails?: PropertyDetails) => {
    if (!newProjectName || !user) return;

    try {
      const propDetails = propertyDetails ? {
        propertyType: propertyDetails.propertyType || undefined,
        propertyAreaSqm: propertyDetails.propertyAreaSqm ? parseFloat(propertyDetails.propertyAreaSqm) : undefined,
        propertyRooms: propertyDetails.propertyRooms ? parseFloat(propertyDetails.propertyRooms) : undefined,
        propertyPrice: propertyDetails.propertyPrice || undefined,
        propertyStatus: propertyDetails.propertyStatus || undefined,
      } : undefined;
      const newProject = await createProject(user.id, newProjectName, newProjectAddr, aspectRatio, stagingStyle, propDetails);

      track('Project Created', { aspect_ratio: aspectRatio, staging_style: stagingStyle });
      setProjects((prev) => [newProject, ...prev]);
      setCurrentProject(newProject);
      setUploadedImages([]);
      setResults([]);
      setBranding(DEFAULT_BRANDING);
      setNewProjectName('');
      setNewProjectAddr('');
      setCurrentView('upload'); // Go directly to upload after creating
    } catch (error) {
      console.error('Fehler beim Erstellen des Projekts:', error);
      alert('Projekt konnte nicht erstellt werden');
    }
  }, [newProjectName, newProjectAddr, user]);

  const handleProjectUpdate = useCallback((updates: Partial<Project>) => {
    if (!currentProject) return;

    const updatedProject = { ...currentProject, ...updates };
    setCurrentProject(updatedProject);
    setProjects(prev => prev.map(p => p.id === currentProject.id ? updatedProject : p));
  }, [currentProject]);

  const handleStagingStyleChange = useCallback(async (style: StagingStyle) => {
    if (!currentProject) return;

    // Optimistic update
    const updatedProject = { ...currentProject, stagingStyle: style };
    setCurrentProject(updatedProject);
    setProjects(prev => prev.map(p => p.id === currentProject.id ? updatedProject : p));

    // Persist to database
    try {
      await updateProject(currentProject.id, { staging_style: style });
      console.log(`[Project] Updated staging style to: ${style}`);
    } catch (error) {
      console.error('Failed to update staging style:', error);
    }
  }, [currentProject]);

  // Hero image save: upload rendered hero to storage + save config, returns public URL
  const handleHeroSave = useCallback(async (imageId: string, heroDataUrl: string, config: HeroOverlayConfig): Promise<string> => {
    if (!currentProject || !user) return '';

    try {
      // Convert data URL to Blob
      const res = await fetch(heroDataUrl);
      const blob = await res.blob();
      const file = new File([blob], `hero-${imageId}-${Date.now()}.png`, { type: 'image/png' });

      // Upload to Supabase Storage
      const storagePath = `projects/${user.id}/${currentProject.id}/${imageId}_hero.png`;
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(storagePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Save config + path to database, clear old hero images
      await saveHeroOverlayConfig(imageId, config);
      await saveHeroImagePath(imageId, storagePath);
      await clearOtherHeroImages(currentProject.id, imageId);

      // Get public URL for immediate display
      const { data: urlData } = supabase.storage.from('images').getPublicUrl(storagePath);

      console.log('[Hero] Saved hero image:', storagePath);
      return urlData.publicUrl;
    } catch (error) {
      console.error('[Hero] Save failed:', error);
      throw error;
    }
  }, [currentProject, user]);

  const handleDeleteProject = useCallback(async (projectId: string) => {
    try {
      await deleteProject(projectId);
      track('Project Deleted');
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      // If we're viewing the deleted project, go back to dashboard
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setCurrentView('dashboard');
      }
    } catch (error) {
      console.error('Fehler beim Löschen des Projekts:', error);
      throw error;
    }
  }, [currentProject]);

  const openProjectDetails = useCallback((project: Project) => {
    setCurrentProject(project);
    setUploadedImages([]);
    setResults([]);
    // Flow v3: Projekt öffnet den Tab-Workspace statt der Scroll-Detailseite
    setCurrentView(flowV3Active ? 'workspace' : 'project-details');
  }, [flowV3Active]);

  const openStagingForProject = useCallback(async () => {
    if (!currentProject) return;
    
    setResults([]);
    setCurrentView('upload');
    
    // Load existing unprocessed images (including tenant uploads)
    try {
      const existingImages = await fetchUnprocessedImagesForStaging(currentProject.id);
      
      if (existingImages.length > 0) {
        // Convert to UploadedImage format
        const uploadedFromDb: UploadedImage[] = existingImages.map((img) => ({
          id: img.id,
          previewUrl: img.previewUrl,
          roomType: img.roomType as RoomType,
          customLabel: img.customLabel,
          keepKitchen: img.keepKitchen,
          dbId: img.dbId,
          storagePath: img.storagePath,
          isFromDatabase: true,
          isTenantUpload: img.isTenantUpload,
        }));
        
        setUploadedImages(uploadedFromDb);
        console.log(`[Staging] Loaded ${existingImages.length} existing images (${existingImages.filter(i => i.isTenantUpload).length} tenant uploads)`);
      } else {
        setUploadedImages([]);
      }
    } catch (error) {
      console.error('[Staging] Failed to load existing images:', error);
      setUploadedImages([]);
    }
  }, [currentProject]);

  const openStagingForProjectWithSelection = useCallback(async (selectedIds: Set<string>) => {
    if (!currentProject) return;

    setResults([]);
    setCurrentView('upload');

    try {
      const existingImages = await fetchUnprocessedImagesForStaging(currentProject.id);

      if (existingImages.length > 0) {
        const uploadedFromDb: UploadedImage[] = existingImages.map((img) => ({
          id: img.id,
          previewUrl: img.previewUrl,
          roomType: img.roomType as RoomType,
          customLabel: img.customLabel,
          keepKitchen: img.keepKitchen,
          dbId: img.dbId,
          storagePath: img.storagePath,
          isFromDatabase: true,
          isTenantUpload: img.isTenantUpload,
          generationMode: selectedIds.has(img.dbId) ? undefined : 'none',
        }));

        setUploadedImages(uploadedFromDb);
      } else {
        setUploadedImages([]);
      }
    } catch (error) {
      console.error('[Staging] Failed to load existing images:', error);
      setUploadedImages([]);
    }
  }, [currentProject]);

  const openOverlayForProject = useCallback(() => {
    setCurrentView('overlay-tool');
  }, []);

  const openFloorplanForProject = useCallback(async () => {
    if (!currentProject) return;
    // Reset first (avoid cross-project state contamination), then hydrate from DB,
    // then navigate. Navigating BEFORE hydration flashes an empty Upload view even
    // though the project has existing state — the bug Till reported 2026-04-24.
    resetFloorplanState();
    await loadExistingFloorplan(currentProject.id);
    // An gespeicherter Position fortsetzen, falls vorhanden (sonst Upload — dort
    // bietet "Weiterarbeiten" den DB-basierten Wiedereinstieg an).
    const session = loadFloorplanSession(currentProject.id);
    setCurrentView(session?.view ?? 'floorplan-upload');
  }, [currentProject, resetFloorplanState, loadExistingFloorplan]);

  // Grundriss-Flow aus der Resume-Bubble fortsetzen (ggf. projektübergreifend):
  // Projekt setzen, DB-Stand laden, an die gespeicherte Schritt-Position springen.
  const resumeFloorplanFlow = useCallback(async (projectId: string) => {
    const proj = projectsRef.current.find((p) => p.id === projectId);
    if (!proj) return;
    const session = loadFloorplanSession(projectId);
    setCurrentProject(proj);
    resetFloorplanState();
    await loadExistingFloorplan(projectId);
    setCurrentView(session?.view ?? 'floorplan-upload');
  }, [resetFloorplanState, loadExistingFloorplan]);

  const openFloorplanBrandingForProject = useCallback(async () => {
    if (!currentProject) return;
    resetFloorplanState();
    await loadExistingFloorplan(currentProject.id);
    setCurrentView('floorplan-branding');
  }, [currentProject, resetFloorplanState, loadExistingFloorplan]);

  // Auto-navigate forward on state transitions (false→true / null→value).
  // Prev-value refs ensure going back doesn't re-trigger navigation.
  const prevUnitExtractionDone = useRef(false);
  const prevGenerated2dUrl = useRef<string | null>(null);

  useEffect(() => {
    const justCompleted = floorplanState.unitExtractionDone && !prevUnitExtractionDone.current;
    prevUnitExtractionDone.current = floorplanState.unitExtractionDone;

    if (currentView === 'floorplan-unit-selection' && justCompleted) {
      setCurrentView('floorplan-labels');
    }
  }, [currentView, floorplanState.unitExtractionDone, floorplanState.isUnitExtracting]);

  useEffect(() => {
    const justArrived = !!floorplanState.generated2dUrl && !prevGenerated2dUrl.current;
    prevGenerated2dUrl.current = floorplanState.generated2dUrl;

    if (currentView === 'floorplan-editor' && justArrived) {
      setCurrentView('floorplan-review');
    }
  }, [currentView, floorplanState.generated2dUrl]);

  // Grundriss-Flow: aktuelle Schritt-Position im Tab-Speicher festhalten, damit
  // sie beim Weg-Navigieren (Sidebar, Overlay-Builder, Browser-Back) erhalten
  // bleibt und "Grundrisserstellung fortsetzen" dorthin zurückführt. Die Daten
  // selbst liegen in der DB — hier nur die UI-Position. (Nicht während der
  // Rehydration schreiben, sonst überschreibt der Restore die echte Position.)
  useEffect(() => {
    if (!user || isRehydrating) return;
    if (currentProject && FLOORPLAN_VIEWS.has(currentView)) {
      saveFloorplanSession({ projectId: currentProject.id, view: currentView as FloorplanFlowView });
    }
  }, [currentView, currentProject, user, isRehydrating]);

  const startNewStaging = useCallback(() => {
    setCurrentProject(null);
    setNewProjectName('');
    setNewProjectAddr('');
    setUploadedImages([]);
    setResults([]);
    if (flowV3Active) {
      // Flow v3: volles Modal mit Tool-Auswahl (Staging vs. Grundriss)
      setNewProjectLockedTool(null);
      setNewProjectModalOpen(true);
    } else {
      setCurrentView('setup');
    }
  }, [flowV3Active]);

  // Flow v3: „Neues Staging" / „Neuer Grundriss" (Sidebar-Unterpunkte) → Modal
  // im „nur Name"-Modus, das direkt in den gewählten Flow springt.
  const startNewProjectForTool = useCallback((tool: NewProjectTool) => {
    setCurrentProject(null);
    setNewProjectName('');
    setNewProjectAddr('');
    setUploadedImages([]);
    setResults([]);
    setNewProjectLockedTool(tool);
    setNewProjectModalOpen(true);
  }, []);

  // Flow v3: „Neues Projekt"-Modal bestätigt → Projekt anlegen und in den
  // gewählten Flow (Staging-Assistent oder Grundriss-Tool) springen.
  const handleNewProjectConfirm = useCallback(async (name: string, tool: 'staging' | 'floorplan') => {
    if (!user?.id) return;
    setIsCreatingNewProject(true);
    try {
      const created = await createProject(user.id, name, '');
      setProjects((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
      setCurrentProject(created);
      setNewProjectModalOpen(false);
      if (tool === 'staging') {
        setUploadedImages([]);
        setResults([]);
        setWizardMode('new');
        setCurrentView('wizard');
      } else {
        resetFloorplanState();
        setCurrentView('floorplan-upload');
      }
    } catch (err) {
      console.error('[NewProject] creation failed:', err);
      alert(err instanceof Error ? err.message : 'Projekt konnte nicht erstellt werden.');
    } finally {
      setIsCreatingNewProject(false);
    }
  }, [user?.id, resetFloorplanState]);

  // Flow v3: offenen Assistenten-Durchlauf fortsetzen (aus der Resume-Bubble).
  // mode 'add' → der Wizard rekonstruiert den Batch aus Session + DB.
  const resumeWizardFlow = useCallback((projectId: string) => {
    const proj = projectsRef.current.find((p) => p.id === projectId);
    if (!proj) return;
    setCurrentProject(proj);
    setResults([]);
    setUploadedImages([]);
    setWizardMode('add');
    setCurrentView('wizard');
  }, []);

  // ============================================================================
  // Image Actions (Upload to Supabase immediately)
  // ============================================================================

  // Core file processing logic — shared by file input and drag & drop
  const processUploadedFiles = useCallback(
    async (rawFiles: File[]) => {
      if (!currentProject || rawFiles.length === 0) return;

      // Convert any HEIC/HEIF files to JPEG for browser compatibility
      const files: File[] = [];
      for (const rawFile of rawFiles) {
        try {
          const converted = await convertHeicToJpeg(rawFile);
          files.push(converted);
        } catch (error) {
          console.error(`[Upload] HEIC conversion failed for ${rawFile.name}:`, error);
          files.push(rawFile);
        }
      }

      // Create placeholder entries with uploading state
      const newImages: UploadedImage[] = files.map((file) => ({
        id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        roomType: RoomType.LIVING,
        keepKitchen: false,
        isUploading: true,
      }));

      setUploadedImages((prev) => [...prev, ...newImages]);
      track('Image Uploaded', { count: files.length, has_heic: rawFiles.some(f => /\.heic$/i.test(f.name)) });
      trackKlaviyo('Photo Uploaded', { source: 'staging', count: files.length, has_heic: rawFiles.some(f => /\.heic$/i.test(f.name)) });

      // Upload each file to Supabase Storage
      for (const img of newImages) {
        if (!img.file) continue;

        try {
          console.log(`[Upload] Uploading ${img.file.name} to Supabase...`);

          const dbImage = await uploadProjectImage(
            currentProject.id,
            img.file,
            img.roomType,
            img.keepKitchen,
            img.customLabel
          );

          setUploadedImages((prev) =>
            prev.map((i) =>
              i.id === img.id
                ? {
                    ...i,
                    dbId: dbImage.id,
                    storagePath: dbImage.storage_path,
                    isUploading: false,
                    uploadError: undefined,
                  }
                : i
            )
          );

          console.log(`[Upload] ✓ Uploaded: ${dbImage.storage_path}`);
        } catch (error) {
          console.error(`[Upload] Failed to upload ${img.file?.name}:`, error);

          setUploadedImages((prev) =>
            prev.map((i) =>
              i.id === img.id
                ? {
                    ...i,
                    isUploading: false,
                    uploadError: error instanceof Error ? error.message : 'Upload fehlgeschlagen',
                  }
                : i
            )
          );
        }
      }
    },
    [currentProject]
  );

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return;
      await processUploadedFiles(Array.from(e.target.files));
    },
    [processUploadedFiles]
  );

  const handleFilesDrop = useCallback(
    async (files: File[]) => {
      await processUploadedFiles(files);
    },
    [processUploadedFiles]
  );

  const updateImageMeta = useCallback(
    async (id: string, updates: Partial<UploadedImage>) => {
      // Update local state first (optimistic update)
      setUploadedImages((prev) =>
        prev.map((img) => (img.id === id ? { ...img, ...updates } : img))
      );

      // If we have a database ID, also update the database
      const image = uploadedImages.find((img) => img.id === id);
      if (image?.dbId) {
        try {
          const dbUpdates: { room_type?: string; custom_label?: string; keep_kitchen?: boolean } = {};
          
          if (updates.roomType !== undefined) {
            dbUpdates.room_type = String(updates.roomType);
          }
          if (updates.customLabel !== undefined) {
            dbUpdates.custom_label = updates.customLabel;
          }
          if (updates.keepKitchen !== undefined) {
            dbUpdates.keep_kitchen = updates.keepKitchen;
          }

          if (Object.keys(dbUpdates).length > 0) {
            await updateImageMetadata(image.dbId, dbUpdates);
            console.log(`[Meta] Updated DB for image ${image.dbId}:`, dbUpdates);
          }
        } catch (error) {
          console.error('Failed to update image metadata in DB:', error);
        }
      }
    },
    [uploadedImages]
  );

  const handleDeleteImage = useCallback(
    async (id: string) => {
      const image = uploadedImages.find((img) => img.id === id);
      if (!image) return;

      // Remove from local state immediately
      setUploadedImages((prev) => prev.filter((img) => img.id !== id));

      // If stored in DB, delete from storage + database
      if (image.dbId) {
        try {
          const storagePaths = [image.storagePath].filter(Boolean) as string[];
          await deleteProjectImage(image.dbId, storagePaths);
        } catch (error) {
          console.error('[Project] Failed to delete image from DB:', error);
          // Restore local state on failure
          setUploadedImages((prev) => [...prev, image]);
        }
      }
    },
    [uploadedImages]
  );

  // ============================================================================
  // Generation Actions (via Edge Function)
  // ============================================================================

  const processImageVariant = useCallback(
    async (img: UploadedImage, type: GenerationType, feedback?: string, sourceImagePath?: string, isRegeneration = false, emptyBasePath?: string, catalogPlan?: import('@/types').CatalogPlan, emptyConsultOverride?: string) => {
      // Ensure image is uploaded to storage
      if (!img.storagePath || !currentProject) {
        console.error('[Generation] Image not uploaded to storage yet');
        setResults((prev) =>
          prev.map((r) =>
            r.originalImageId === img.id && r.type === type
              ? { ...r, isLoading: false, error: 'Bild wurde noch nicht hochgeladen' }
              : r
          )
        );
        return;
      }

      // Set loading state
      setResults((prev) =>
        prev.map((r) =>
          r.originalImageId === img.id && r.type === type
            ? { ...r, isLoading: true, isSaving: false, error: undefined }
            : r
        )
      );

      try {
        track('Staging Started', { room_type: img.roomType, staging_type: type, is_regeneration: isRegeneration });
        console.log(`[Generation] Calling Edge Function for ${type}...`);
        console.log(`[Generation] Storage path: ${img.storagePath}`);
        console.log(`[Generation] DB Image ID: ${img.dbId}`);
        console.log(`[Generation] Source image (refinement): ${sourceImagePath || 'none'}`);

        // Erstgenerierung: 2 Varianten parallel, Regenerierung: 1 Variante
        const vCount = isRegeneration ? 1 : 2;

        const callGenerate = () => generateRoomImage(
          img.storagePath!,
          currentProject.id,
          img.roomType,
          type,
          img.keepKitchen,
          feedback,
          img.customLabel,
          img.dbId,
          sourceImagePath,
          user?.id,
          currentProject.aspectRatio,
          currentProject.stagingStyle,
          undefined, // adminTest
          vCount,
          emptyBasePath,
          catalogPlan,
          emptyConsultOverride,
        );

        let response;
        try {
          response = await callGenerate();
        } catch (firstErr) {
          // Auto-retry once on first generation (not on manual regeneration)
          if (!isRegeneration) {
            console.warn('[Generation] First attempt failed, retrying in 2s...', firstErr);
            await new Promise(r => setTimeout(r, 2000));
            response = await callGenerate();
          } else {
            throw firstErr;
          }
        }

        if (!response.success || !response.imageUrl) {
          throw new Error(response.error || 'Generation fehlgeschlagen');
        }

        console.log(`[Generation] ✓ Generated ${type} image (${response.variants?.length ?? 1} variants)`);
        track('Staging Completed', { room_type: img.roomType, staging_type: type, variants: response.variants?.length ?? 1 });

        // Refresh credits
        if (user?.id) {
          await new Promise(resolve => setTimeout(resolve, 500));
          await refreshCredits();
        }

        // Update result with generated image(s)
        setResults((prev) =>
          prev.map((r) => {
            if (r.originalImageId === img.id && r.type === type) {
              // Build variants array
              if (response.variants && response.variants.length > 1) {
                // Multiple variants from parallel generation
                const newVariants = response.variants.map((v, i) => ({
                  id: `variant_${Date.now()}_${i}`,
                  url: v.imageUrl,
                  storagePath: v.storagePath,
                  modelUsed: v.modelUsed,
                  promptVariant: v.promptVariant,
                }));
                return {
                  ...r,
                  isLoading: false,
                  isSaving: false,
                  variants: newVariants,
                  selectedVariantId: newVariants[0].id,
                  currentUrl: newVariants[0].url,
                  storagePath: newVariants[0].storagePath,
                  promptUsed: response.promptUsed,
                  abTestId: response.abTestId,
                  catalogDebug: response.catalogDebug ?? r.catalogDebug,
                  history: [
                    ...r.history,
                    ...newVariants.map(v => ({
                      id: v.id,
                      url: v.url,
                      timestamp: Date.now(),
                      feedbackUsed: feedback,
                      storagePath: v.storagePath,
                    })),
                  ],
                };
              }

              // Single image (regeneration) — append as new variant
              const newVariant = {
                id: `variant_${Date.now()}_0`,
                url: response.imageUrl!,
                storagePath: response.storagePath!,
              };
              const existingVariants = r.variants || [];
              return {
                ...r,
                isLoading: false,
                isSaving: false,
                variants: [...existingVariants, newVariant],
                selectedVariantId: newVariant.id,
                currentUrl: newVariant.url,
                storagePath: newVariant.storagePath,
                promptUsed: response.promptUsed,
                catalogDebug: response.catalogDebug ?? r.catalogDebug,
                history: [
                  ...r.history,
                  {
                    id: newVariant.id,
                    url: newVariant.url,
                    timestamp: Date.now(),
                    feedbackUsed: feedback,
                    storagePath: newVariant.storagePath,
                  },
                ],
              };
            }
            return r;
          })
        );
      } catch (err) {
        const error = err as Error;
        console.error(`[Generation] Error:`, error);
        
        setResults((prev) =>
          prev.map((r) =>
            r.originalImageId === img.id && r.type === type
              ? { ...r, isLoading: false, isSaving: false, error: error.message || 'Generation fehlgeschlagen.' }
              : r
          )
        );
      }
    },
    [currentProject, user]
  );

  // staged-only: erzeugt eine Leer-Basis, ohne sie als Ergebnis anzuzeigen; gibt den Storage-Pfad zurück.
  const generateEmptyBaseSilently = useCallback(
    async (img: UploadedImage): Promise<string | null> => {
      if (!currentProject) return null;
      try {
        const res = await generateRoomImage(
          img.storagePath!, currentProject.id, img.roomType, GenerationType.EMPTY,
          img.keepKitchen, undefined, img.customLabel, img.dbId, undefined,
          user?.id, currentProject.aspectRatio, currentProject.stagingStyle, undefined, 1,
        );
        return res.storagePath ?? null;
      } catch (e) {
        console.error('[Generation] silent empty base failed', e);
        return null;
      }
    },
    [currentProject, user?.id],
  );

  const startGeneration = useCallback(async () => {
    // Check if all images are uploaded
    const notUploaded = uploadedImages.filter(img => !img.storagePath || img.isUploading);
    if (notUploaded.length > 0) {
      alert(`${notUploaded.length} Bilder werden noch hochgeladen. Bitte warten.`);
      return;
    }

    setCurrentView('review');

    // Initialize result placeholders with dbImageId for proper linking
    const newResults: GenerationResult[] = [];
    uploadedImages.forEach((img) => {
      const canStage = supportsStaging(img.roomType as string);
      const mode = effectiveUploadMode(img.generationMode, catalogStagingActive, canStage);

      if (mode === 'none') return; // Skip — no generation for this image

      // Create EMPTY result if applicable
      const wantsEmpty = mode === 'both' || mode === 'empty-only';
      if (wantsEmpty && !results.find((r) => r.originalImageId === img.id && r.type === GenerationType.EMPTY)) {
        newResults.push({
          originalImageId: img.id,
          dbImageId: img.dbId,
          type: GenerationType.EMPTY,
          currentUrl: null,
          history: [],
          isLoading: true,
        });
      }

      // Create STAGED result if applicable.
      // v2 gate ON: 'both' staged erscheint erst nach handleStageApproved.
      // v2 gate OFF (Flag aus): Legacy — 'both' erzeugt Staged sofort parallel.
      const wantsStaged = catalogStagingActive
        ? (mode === 'staged-only' && canStage)
        : ((mode === 'both' || mode === 'staged-only') && canStage);
      if (wantsStaged && !results.find((r) => r.originalImageId === img.id && r.type === GenerationType.STAGED)) {
        newResults.push({
          originalImageId: img.id,
          dbImageId: img.dbId,
          type: GenerationType.STAGED,
          currentUrl: null,
          history: [],
          isLoading: true,
        });
      }
    });

    setResults((prev) => [...prev, ...newResults]);

    // Throttled parallel execution
    const tasks: (() => Promise<void>)[] = [];
    for (const img of uploadedImages) {
      const canStage = supportsStaging(img.roomType as string);
      const mode = effectiveUploadMode(img.generationMode, catalogStagingActive, canStage);

      if (mode === 'none') continue;

      if (mode === 'both' || mode === 'empty-only') {
        tasks.push(() => processImageVariant(img, GenerationType.EMPTY));
      }
      if (catalogStagingActive) {
        // v2 gate ON: 'both' Staged wird per handleStageApproved nachgeschoben.
        // 'staged-only': intern Leer erzeugen und automatisch darauf stagen (kein Gate).
        if (mode === 'staged-only' && canStage) {
          tasks.push(async () => {
            const emptyPath = await generateEmptyBaseSilently(img);
            if (emptyPath) await processImageVariant(img, GenerationType.STAGED, undefined, undefined, false, emptyPath);
          });
        }
      } else {
        // Legacy (Flag aus): 'both' + 'staged-only' stagen sofort das Original, parallel zum Leer.
        if ((mode === 'both' || mode === 'staged-only') && canStage) {
          tasks.push(() => processImageVariant(img, GenerationType.STAGED));
        }
      }
    }

    const active = new Set<Promise<void>>();

    for (const task of tasks) {
      if (active.size >= CONCURRENCY_LIMIT) {
        await Promise.race(active);
      }

      const promise = task();
      active.add(promise);
      promise.finally(() => active.delete(promise));
    }

    await Promise.all(active);
  }, [uploadedImages, results, processImageVariant, generateEmptyBaseSilently, catalogStagingActive]);

  const handleRegenerate = useCallback(
    async (imgId: string, type: GenerationType, feedback: string | null, sourceImagePath?: string, emptyConsultOverride?: string) => {
      const img = uploadedImages.find((i) => i.id === imgId);
      if (!img) return;
      // Staged re-roll (no feedback): stage on the approved empty base, not the original photo.
      // Refinements (feedback present) edit the current staged image via sourceImagePath instead.
      let emptyBasePath: string | undefined;
      if (catalogStagingActive && type === GenerationType.STAGED && !feedback) {
        const emptyRes = results.find((r) => r.originalImageId === imgId && r.type === GenerationType.EMPTY);
        emptyBasePath = emptyRes?.storagePath ?? undefined;
      }
      // isRegeneration = true → only 1 variant, appended to existing
      await processImageVariant(img, type, feedback ?? undefined, sourceImagePath, true, emptyBasePath, undefined, emptyConsultOverride);
    },
    [uploadedImages, results, processImageVariant, catalogStagingActive]
  );

  // 'both': bestätigt die gewählte Leer-Variante und schiebt den Staged-Task nach (Basis = Leer-Bild).
  const handleStageApproved = useCallback(
    async (imgId: string, opts?: { catalogPlan?: import('@/types').CatalogPlan }): Promise<void> => {
      const img = uploadedImages.find((i) => i.id === imgId);
      if (!img) return;
      const emptyRes = results.find((r) => r.originalImageId === imgId && r.type === GenerationType.EMPTY);
      const approvedEmptyPath = emptyRes?.storagePath;
      if (!approvedEmptyPath) return;

      // Bugfix: gewählte Leer-Variante in storage_path_empty persistieren (Edge schreibt nur variant[0]).
      if (img.dbId) {
        void supabase.from('images').update({ storage_path_empty: approvedEmptyPath }).eq('id', img.dbId);
      }

      // Staged-Result-Platzhalter jetzt erzeugen (Eingerichtet-Spalte erscheint).
      setResults((prev) =>
        prev.find((r) => r.originalImageId === imgId && r.type === GenerationType.STAGED)
          ? prev
          : [...prev, { originalImageId: imgId, dbImageId: img.dbId, type: GenerationType.STAGED, currentUrl: null, history: [], isLoading: true }],
      );

      await processImageVariant(img, GenerationType.STAGED, undefined, undefined, false, approvedEmptyPath, opts?.catalogPlan);
    },
    [uploadedImages, results, processImageVariant],
  );

  // Admin: Trockenlauf-Analyse (kein Bild, kein Credit) — liefert CatalogDebug inkl. plan
  // für das CatalogAnalyzePanel, basierend auf der bereits freigegebenen Leer-Variante.
  const handleCatalogAnalyze = useCallback(
    async (imgId: string, consultOverride: string, renderTemplateOverride: string) => {
      const img = uploadedImages.find((i) => i.id === imgId);
      if (!img) return null;
      const emptyRes = results.find((r) => r.originalImageId === imgId && r.type === GenerationType.EMPTY);
      const approvedEmptyPath = emptyRes?.storagePath;
      if (!approvedEmptyPath) return null;
      return analyzeCatalogStaging({
        emptyStoragePath: approvedEmptyPath,
        projectId: currentProject?.id,
        roomType: String(img.roomType),
        keepKitchen: img.keepKitchen,
        userId: user?.id,
        aspectRatio: currentProject?.aspectRatio,
        stagingStyle: currentProject?.stagingStyle,
        consultOverride,
        renderTemplateOverride,
      });
    },
    [uploadedImages, results, currentProject, user],
  );

  const handleVariantSelect = useCallback(
    (imgId: string, type: GenerationType, variantId: string) => {
      setResults(prev => prev.map(r => {
        if (r.originalImageId === imgId && r.type === type && r.variants) {
          const variant = r.variants.find(v => v.id === variantId);
          if (variant) {
            return {
              ...r,
              selectedVariantId: variantId,
              currentUrl: variant.url,
              storagePath: variant.storagePath,
            };
          }
        }
        return r;
      }));
    },
    []
  );

  // Track A/B selections when user proceeds to the next step
  const trackVariantSelections = useCallback(() => {
    for (const r of results) {
      if (r.variants && r.variants.length > 1 && r.selectedVariantId) {
        const selected = r.variants.find(v => v.id === r.selectedVariantId);
        if (selected?.modelUsed) {
          const img = uploadedImages.find(i => i.id === r.originalImageId);
          supabase.from('model_selections').insert({
            user_id: user?.id,
            model_selected: selected.modelUsed,
            generation_type: r.type,
            room_type: img?.roomType || 'unknown',
            prompt_variant: selected.promptVariant || null,
            ab_test_id: r.abTestId || null,
          }).then(() => {}, () => {}); // fire-and-forget
        }
      }
    }
  }, [results, uploadedImages, user]);

  // ============================================================================
  // Single Image Download
  // ============================================================================

  const handleDownloadSingle = useCallback(async (imageUrl: string, filename: string) => {
    track('Image Downloaded', { format: 'single', compressed: defaultDownloadCompressed });
    trackKlaviyo('Image Downloaded', { format: 'single', compressed: defaultDownloadCompressed });
    try {
      if (defaultDownloadCompressed && currentProject?.aspectRatio) {
        // Compress before downloading
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        const compressed = await compressForExport(dataUrl, currentProject.aspectRatio);
        const compressedFilename = filename.replace(/\.(png|jpg|jpeg)$/i, '.jpg');
        const link = document.createElement('a');
        link.href = compressed;
        link.download = compressedFilename;
        link.click();
      } else {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        saveAs(blob, filename);
      }
    } catch (error) {
      console.error('Download fehlgeschlagen:', error);
      // Fallback for base64
      if (imageUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = filename;
        link.click();
      }
    }
  }, [defaultDownloadCompressed, currentProject?.aspectRatio]);

  // ============================================================================
  // Upscale Actions
  // ============================================================================

  const handleUpscaleSelected = useCallback(async (selections: Array<{
    imageId: string;
    variant: 'staged' | 'empty';
    storagePath: string;
    sourceUrl: string;
  }>) => {
    if (!user || !currentProject) return;

    setIsUpscaling(true);
    track('Upscale Started', { count: selections.length });

    // Track results locally so we can update `results` after all complete
    const finalResults = new Map<string, UpscaleResult>();

    const tasks = selections.map((sel) => async () => {
      const key = `${sel.imageId}_${sel.variant}`;

      // Set loading state
      const loadingResult: UpscaleResult = {
        imageId: sel.imageId,
        variant: sel.variant,
        sourceUrl: sel.sourceUrl,
        isUpscaling: true,
      };
      setUpscaleResults((prev) => {
        const next = new Map(prev);
        next.set(key, loadingResult);
        return next;
      });

      try {
        const response = await upscaleImageWithPolling(
          sel.storagePath,
          currentProject.id,
          sel.imageId,
          sel.variant,
          user.id,
        );

        const doneResult: UpscaleResult = {
          imageId: sel.imageId,
          variant: sel.variant,
          sourceUrl: sel.sourceUrl,
          isUpscaling: false,
          upscaledUrl: response.imageUrl,
        };
        finalResults.set(key, doneResult);

        setUpscaleResults((prev) => {
          const next = new Map(prev);
          next.set(key, doneResult);
          return next;
        });
      } catch (err) {
        const errorResult: UpscaleResult = {
          imageId: sel.imageId,
          variant: sel.variant,
          sourceUrl: sel.sourceUrl,
          isUpscaling: false,
          error: err instanceof Error ? err.message : 'Upscaling fehlgeschlagen',
        };
        finalResults.set(key, errorResult);

        setUpscaleResults((prev) => {
          const next = new Map(prev);
          next.set(key, errorResult);
          return next;
        });
      }
    });

    // Execute with concurrency limit
    const active = new Set<Promise<void>>();
    for (const task of tasks) {
      if (active.size >= CONCURRENCY_LIMIT) {
        await Promise.race(active);
      }
      const promise = task();
      active.add(promise);
      promise.finally(() => active.delete(promise));
    }
    await Promise.all(active);

    // Update results array to use upscaled URLs for branding pipeline
    setResults((prev) =>
      prev.map((r) => {
        const key = `${r.dbImageId}_${r.type === GenerationType.EMPTY ? 'empty' : 'staged'}`;
        const ur = finalResults.get(key);
        if (ur?.upscaledUrl) {
          return { ...r, currentUrl: ur.upscaledUrl };
        }
        return r;
      })
    );

    setIsUpscaling(false);

    // Refresh credits
    if (user.id) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await refreshCredits();
    }
  }, [currentProject, user, refreshCredits]);

  // ============================================================================
  // Branding Actions
  // ============================================================================

  const handleBrandingUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, field: 'logo' | 'overlay') => {
      if (!e.target.files?.[0]) return;

      const file = e.target.files[0];
      const url = URL.createObjectURL(file);

      setBranding((prev) => ({
        ...prev,
        [field]: file,
        [`${field}Url`]: url,
      }));
    },
    []
  );

  const updateBranding = useCallback((updates: Partial<BrandingConfig>) => {
    setBranding((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDownloadAll = useCallback(async (compressed: boolean = false) => {
    track('Image Downloaded', { format: 'zip', compressed });
    trackKlaviyo('Image Downloaded', { format: 'zip', compressed });
    setIsProcessingBranding(true);
    const imgExt = compressed ? 'jpg' : 'png';
    const aspectRatio = currentProject?.aspectRatio || '4:3';

    // Collect branded images for auto-save (both staged AND empty)
    const brandedImagesForUpload: Array<{
      base64: string;
      roomType: string;
      originalId: string;
      variant: 'staged' | 'empty';
    }> = [];

    // Dateiendung der Original-Bytes (werden ohne Re-Encoding exportiert)
    const originalExt = (name?: string): 'png' | 'jpg' =>
      name?.toLowerCase().endsWith('.png') ? 'png' : 'jpg';

    try {
      // Gleiches flaches Namensschema wie alle anderen Export-Flows
      // (Immobilienbilder/Wohnzimmer_01_eingerichtet.png).
      const entries: ExportEntry[] = [];
      for (const img of uploadedImages) {
        const roomLabel = img.customLabel || getRoomLabel(img.roomType);

        // Original unverändert (Storage-URL bevorzugt, sonst lokale Datei)
        if (img.storagePath) {
          entries.push({
            imageId: img.id, roomLabel, variant: 'original',
            source: getPublicUrl(img.storagePath), ext: originalExt(img.file?.name ?? img.storagePath),
          });
        } else if (img.file) {
          entries.push({
            imageId: img.id, roomLabel, variant: 'original',
            source: img.file, ext: originalExt(img.file.name),
          });
        }

        // Generierte Ergebnisse mit eingebranntem Branding
        for (const res of results.filter((r) => r.originalImageId === img.id)) {
          if (!res.currentUrl) continue;
          const brandedUrl = await renderBrandedExport(res.currentUrl, {
            logoUrl: branding.logoUrl,
            overlayUrl: branding.overlayUrl,
            logoPosition: branding.logoPosition,
            watermarkUrl: branding.watermarkUrl,
            watermarkOpacity: branding.watermarkOpacity,
            watermarkPosition: branding.watermarkPosition,
            watermarkScale: branding.watermarkScale,
          });
          // Auto-save to Supabase always in original resolution
          if (currentProject && img.dbId) {
            brandedImagesForUpload.push({
              base64: brandedUrl,
              roomType: img.customLabel || String(img.roomType),
              originalId: img.dbId,
              variant: res.type === GenerationType.EMPTY ? 'empty' : 'staged',
            });
          }
          entries.push({
            imageId: img.id, roomLabel,
            variant: res.type === GenerationType.EMPTY ? 'leer' : 'eingerichtet',
            source: compressed ? await compressForExport(brandedUrl, aspectRatio) : brandedUrl,
            ext: imgExt,
          });
        }
      }

      // Auto-save branded images to Supabase (both staged and empty)
      if (currentProject && brandedImagesForUpload.length > 0) {
        console.log(`Uploading ${brandedImagesForUpload.length} branded images to Supabase...`);
        const { uploaded, failed } = await uploadBrandedImagesToSupabase(
          currentProject.id,
          brandedImagesForUpload
        );
        console.log(`✓ Branded images saved: ${uploaded} uploaded, ${failed} failed`);
      }

      // KI-Hinweis zuletzt: die Ergebnisse tragen hier bereits Branding +
      // Kompression, die Originale bleiben per applyAiLabel unberührt.
      const labeled = aiLabel.enabled ? await applyAiLabel(entries, [], aiLabel.position, aiLabel.opacity) : { entries, floorplans: [] };
      // data:-URLs sind fetchbar → buildExportZip lädt sie direkt
      const result = await buildExportZip(labeled.entries, labeled.floorplans);
      saveAs(result.blob, exportZipFileName(currentProject?.name || 'estage'));
      if (result.failed > 0) {
        alert(`${result.added} von ${result.total} Dateien exportiert — einige Bilder konnten nicht geladen werden.`);
      }
    } catch (e) {
      console.error('Error creating ZIP:', e);
      // Abbrechen statt still ohne Wasserzeichen/Logo/Overlay zu exportieren.
      alert(
        e instanceof BrandingLayerError
          ? 'Das Branding (z. B. Wasserzeichen) konnte nicht angewendet werden — Download abgebrochen. Tipp: dunkles Logo auf weißem oder transparentem Hintergrund verwenden, oder die Auswahl entfernen.'
          : 'Fehler beim Erstellen der ZIP-Datei'
      );
    } finally {
      setIsProcessingBranding(false);
    }
  }, [currentProject, uploadedImages, results, branding, aiLabel.enabled, aiLabel.position, aiLabel.opacity]);

  // ============================================================================
  // Save Branding and Close (without downloading ZIP)
  // ============================================================================

  const handleSaveAndClose = useCallback(async () => {
    // Check if any branding is configured
    const hasBranding = branding.logoUrl || branding.overlayUrl || branding.watermarkUrl;
    
    if (!hasBranding) {
      // No branding configured, just navigate
      console.log('[Save] No branding configured, navigating to project details');
      setCurrentView('project-details');
      return;
    }

    if (!currentProject) {
      console.warn('[Save] No current project, cannot save');
      setCurrentView('project-details');
      return;
    }

    setIsProcessingBranding(true);
    console.log('[Save] Starting branded images save...');

    // Collect branded images for upload (both staged AND empty)
    const brandedImagesForUpload: Array<{
      base64: string;
      roomType: string;
      originalId: string;
      variant: 'staged' | 'empty';
    }> = [];
    let brandingFailures = 0;

    try {
      for (const img of uploadedImages) {
        // Skip images without database ID
        if (!img.dbId) {
          console.warn(`[Save] Skipping image ${img.id} - no database ID`);
          continue;
        }

        // Get all results for this image
        const imgResults = results.filter((r) => r.originalImageId === img.id);
        
        for (const res of imgResults) {
          if (res.currentUrl) {
            console.log(`[Save] Processing ${res.type} for ${img.roomType}...`);

            // Apply branding to generate final image
            const brandedResult = await applyBrandingDetailed(res.currentUrl, {
              logoUrl: branding.logoUrl,
              overlayUrl: branding.overlayUrl,
              logoPosition: branding.logoPosition,
              watermarkUrl: branding.watermarkUrl,
              watermarkOpacity: branding.watermarkOpacity,
              watermarkPosition: branding.watermarkPosition,
              watermarkScale: branding.watermarkScale,
            });
            if (brandedResult.failedLayers.length > 0) {
              // Nicht als „gebrandet" speichern, wenn ein Layer fehlte.
              brandingFailures++;
              continue;
            }

            // Collect for upload
            brandedImagesForUpload.push({
              base64: brandedResult.url,
              roomType: img.customLabel || String(img.roomType),
              originalId: img.dbId,
              variant: res.type === GenerationType.EMPTY ? 'empty' : 'staged',
            });
          }
        }
      }

      if (brandingFailures > 0) {
        alert(
          'Das Branding (z. B. Wasserzeichen) konnte nicht auf alle Bilder angewendet werden — diese wurden nicht als gebrandete Version gespeichert. Tipp: dunkles Logo auf weißem oder transparentem Hintergrund verwenden.'
        );
      }

      // Upload all branded images to Supabase
      if (brandedImagesForUpload.length > 0) {
        console.log(`[Save] Uploading ${brandedImagesForUpload.length} branded images...`);
        const { uploaded, failed } = await uploadBrandedImagesToSupabase(
          currentProject.id,
          brandedImagesForUpload
        );
        console.log(`[Save] ✓ Complete: ${uploaded} uploaded, ${failed} failed`);
        
        if (failed > 0) {
          alert(`${failed} Bilder konnten nicht gespeichert werden. ${uploaded} wurden erfolgreich gespeichert.`);
        }
      } else {
        console.log('[Save] No images to upload');
      }

      // Navigate to project details
      setCurrentView('project-details');
    } catch (e) {
      console.error('[Save] Error saving branded images:', e);
      alert('Fehler beim Speichern der Bilder. Bitte versuche es erneut.');
    } finally {
      setIsProcessingBranding(false);
    }
  }, [currentProject, uploadedImages, results, branding]);

  // ============================================================================
  // Floorplan Branding
  // ============================================================================

  const handleNavigateToFloorplanBranding = useCallback(() => {
    setCurrentView('floorplan-branding');
  }, []);

  const handleFloorplanSaveAndClose = useCallback(async () => {
    // Flow bewusst abgeschlossen → Resume-Trail verwerfen (nächster Einstieg startet
    // frisch beim Upload; der DB-Stand bleibt via "Weiterarbeiten" erreichbar).
    if (currentProject) clearFloorplanSession(currentProject.id);
    setCurrentView(flowV3Active ? 'workspace' : 'project-details');
  }, [flowV3Active, currentProject]);

  // Infer which floorplan step to resume on based on the current state shape.
  // Used by FloorplanUploadView's "Weiterarbeiten" button when the user returns
  // to a project with existing generations.
  //
  // D.2 note: active_version_id / active_message_number pointers don't change
  // the step itself — they track the in-Review sub-state (which version/message
  // the user was last viewing). The primary step signals remain generated2dUrl
  // / generated3dUrl / branded2dUrl. activeVersionId is used as a safety
  // fallback: if a version exists but generated2dUrl is somehow null (edge
  // case — e.g. createVersion succeeded but the edge function's DB write of
  // storage_path_floorplan_2d was lost), we still know Review was reached.
  const handleContinueExistingFloorplan = useCallback(() => {
    if (floorplanState.branded2dUrl) {
      setCurrentView('floorplan-branding');
    } else if (floorplanState.generated3dUrl) {
      setCurrentView('floorplan-branding');
    } else if (floorplanState.generated2dUrl) {
      setCurrentView('floorplan-3d');
    } else if (floorplanState.activeVersionId || floorplanState.versions.length > 0) {
      // D.2 safety: version exists but no generated2dUrl — Review was reached
      setCurrentView('floorplan-review');
    } else if (floorplanState.extractedUnitUrl) {
      setCurrentView('floorplan-editor');
    } else {
      // Fallback — shouldn't happen if button is rendered, but keeps us on upload
      setCurrentView('floorplan-upload');
    }
  }, [
    floorplanState.branded2dUrl,
    floorplanState.generated3dUrl,
    floorplanState.generated2dUrl,
    floorplanState.activeVersionId,
    floorplanState.versions.length,
    floorplanState.extractedUnitUrl,
  ]);

  // ============================================================================
  // Sidebar Links
  // ============================================================================

  // First-run nudge: gently pulse the "Neues Projekt" CTA while the user has no
  // projects yet (and we're past the initial load, so it never flashes).
  const hasNoProjects = !isLoadingProjects && projects.length === 0;

  const sidebarLinks: SidebarLink[] = [
    {
      label: 'Projekte',
      icon: <LayoutDashboard className="h-5 w-5 flex-shrink-0" />,
      onClick: () => navigateTo('dashboard'),
      active: currentView === 'dashboard' || currentView === 'project-details' || currentView === 'workspace' || currentView === 'wizard',
    },
    {
      label: 'Neues Projekt',
      icon: <Wand2 className="h-5 w-5 flex-shrink-0" />,
      onClick: startNewStaging,
      active: currentView === 'setup' && !currentProject,
      highlight: hasNoProjects,
      // Flow v3: Direkt-Einstiege ins jeweilige Tool (Modal nur mit Namensfeld) +
      // „Overlay anwenden" (Overlay-Tool) als Unterpunkt.
      children: [
        ...(flowV3Active
          ? [
              {
                label: 'Neues Staging',
                icon: <Sparkles className="h-4 w-4 flex-shrink-0" />,
                onClick: () => startNewProjectForTool('staging'),
              },
              {
                label: 'Neuer Grundriss',
                icon: <LayoutGrid className="h-4 w-4 flex-shrink-0" />,
                onClick: () => startNewProjectForTool('floorplan'),
                featureKey: 'floorplan',
              },
            ]
          : []),
        {
          label: 'Overlay anwenden',
          icon: <Layers className="h-4 w-4 flex-shrink-0" />,
          onClick: () => {
            setCurrentProject(null);
            navigateTo('overlay-tool');
          },
          active: currentView === 'overlay-tool' && !currentProject,
          featureKey: 'overlay_tool',
        },
      ],
    },
    // Overlay Builder: aus der Sidebar entfernt (Meeting 2026-07-07, entschlacken) —
    // erreichbar über Einstellungen → Branding sowie aus Wizard/Workspace-Export.
    {
      label: 'Guthaben',
      icon: <Coins className="h-5 w-5 flex-shrink-0" />,
      onClick: () => navigateTo('credits'),
      active: currentView === 'credits',
      badge: isLoadingCredits ? '...' : formatCredits(credits),
      children: [
        {
          label: 'Partnerprogramm',
          icon: <Gift className="h-4 w-4 flex-shrink-0" />,
          onClick: () => navigateTo('affiliate'),
          active: currentView === 'affiliate',
          featureKey: 'affiliate',
        },
      ],
    },
    // Hilfe & Tutorials - nur wenn Videos existieren (Admins sehen den Link immer)
    ...(tutorials.length > 0 || isAdmin ? [{
      label: 'Hilfe & Tutorials',
      icon: <HelpCircle className="h-5 w-5 flex-shrink-0" />,
      onClick: () => navigateTo('help'),
      active: currentView === 'help',
    }] : []),
    {
      label: 'Einstellungen',
      icon: <Settings className="h-5 w-5 flex-shrink-0" />,
      onClick: () => navigateTo('settings'),
      active: currentView === 'settings',
    },
    // Admin Link - nur für Admins sichtbar
    ...(isAdmin ? [{
      label: 'Admin',
      icon: <Shield className="h-5 w-5 flex-shrink-0" />,
      onClick: () => navigateTo('admin'),
      active: currentView === 'admin',
    }] : []),
    // CRM Link - nur für Admins, öffnet externes CRM
    ...(isAdmin ? [{
      label: 'CRM',
      icon: <ExternalLink className="h-5 w-5 flex-shrink-0" />,
      onClick: () => window.open('https://estage-crm.vercel.app', '_blank'),
      active: false,
    }] : []),
  ];

  // Filter out disabled features for non-admin users
  const visibleSidebarLinks = isAdmin
    ? sidebarLinks
    : sidebarLinks.filter(link => !link.featureKey || !disabledFeatures.includes(link.featureKey));

  // View guard: redirect non-admin users away from disabled feature views
  useEffect(() => {
    if (isAdmin || !disabledFeatures.length) return;
    const viewToFeature: Record<string, string> = {
      'overlay-tool': 'overlay_tool',
      'overlay-builder': 'overlay_builder',
      'affiliate': 'affiliate',
      'floorplan-upload': 'floorplan',
      'floorplan-unit-selection': 'floorplan',
      'floorplan-labels': 'floorplan',
      'floorplan-editor': 'floorplan',
      'floorplan-review': 'floorplan',
      'floorplan-3d': 'floorplan',
      'floorplan-branding': 'floorplan',
    };
    const fk = viewToFeature[currentView];
    if (fk && disabledFeatures.includes(fk)) {
      navigateTo('dashboard');
    }
  }, [currentView, disabledFeatures, isAdmin, navigateTo]);

  // ============================================================================
  // Render
  // ============================================================================

  // Show loading spinner during auth check
  if (tenantUploadToken) {
    return <TenantUploadView token={tenantUploadToken} />;
  }

  if (adminTryoutListId) {
    return (
      <AdminRoute onAccessDenied={() => { window.location.href = '/'; }}>
        <TryoutListDetailView
          listId={adminTryoutListId}
          onBack={() => {
            window.history.pushState({}, '', '/');
            window.location.href = '/';
          }}
        />
      </AdminRoute>
    );
  }

  if (tryoutToken) {
    return <TryoutView token={tryoutToken} />;
  }

  if (showBlog) {
    return <BlogRouter onNavigateHome={() => { window.history.pushState({}, '', '/'); setShowBlog(false); }} />;
  }

  const navigateHome = () => {
    // Skip pushState if we're already on / — navigateToLanding may have already
    // pushed `/#section` before calling us as closeCurrentView, and pushing `/`
    // here would overwrite the hash and prevent the scrollIntoView.
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
    setShowBlog(false);
    setShowDatenschutz(false);
    setShowImpressum(false);
  };

  const navigateToBlog = () => {
    window.history.pushState({}, '', '/blog');
    setShowDatenschutz(false);
    setShowImpressum(false);
    setShowBlog(true);
  };

  const navigateToDatenschutz = () => {
    window.history.pushState({}, '', '/datenschutz');
    setShowBlog(false);
    setShowImpressum(false);
    setShowDatenschutz(true);
  };

  const navigateToImpressum = () => {
    window.history.pushState({}, '', '/impressum');
    setShowBlog(false);
    setShowDatenschutz(false);
    setShowImpressum(true);
  };

  if (showDatenschutz) {
    return (
      <DatenschutzView
        onNavigateHome={navigateHome}
        onBlogClick={navigateToBlog}
        onImpressumClick={navigateToImpressum}
      />
    );
  }

  if (showImpressum) {
    return (
      <ImpressumView
        onNavigateHome={navigateHome}
        onBlogClick={navigateToBlog}
        onDatenschutzClick={navigateToDatenschutz}
      />
    );
  }

  if (authLoading) {
    // No spinner here — the LandingPage splash screen is the loading animation.
    return <div className="min-h-screen bg-background" />;
  }

  // IMPORTANT: Update password view must be shown REGARDLESS of auth state
  // because Supabase auto-logs in the user via the recovery link
  if (authView === 'update-password') {
    return (
      <UpdatePasswordView 
        onSuccess={handlePasswordUpdateSuccess} 
      />
    );
  }

  // Show auth views if not authenticated
  if (!user) {
    if (authView === 'forgot-password') {
      return (
        <ForgotPasswordView
          onBackToLogin={() => navigateToAuthView('login')}
        />
      );
    }

    if (!LANDING_PAGE_ENABLED) {
      return (
        <LoginView
          onForgotPassword={() => navigateToAuthView('forgot-password')}
          initialTab={window.location.pathname === '/signup' ? 'signup' : 'signin'}
          onDatenschutzClick={navigateToDatenschutz}
          onImpressumClick={navigateToImpressum}
        />
      );
    }

    return (
      <LandingPage
        onForgotPassword={() => navigateToAuthView('forgot-password')}
        initialShowLogin={window.location.pathname === '/login' || window.location.pathname === '/signup'}
        initialAuthTab={window.location.pathname === '/signup' ? 'signup' : 'signin'}
        onBlogClick={navigateToBlog}
        onDatenschutzClick={navigateToDatenschutz}
        onImpressumClick={navigateToImpressum}
      />
    );
  }

  // Mobile gate — only for authenticated (dashboard) users; public pages pass through above
  if (isMobile) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8 text-center gap-6">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-8 h-8 text-primary" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
          </svg>
        </div>
        <div className="space-y-2 max-w-xs">
          <h1 className="text-xl font-bold text-foreground">Desktop erforderlich</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            EstateVisionAI ist aktuell nur auf Desktop-Geräten verfügbar. Bitte öffne die App auf einem Computer oder Laptop.
          </p>
        </div>
      </div>
    );
  }

  // Block render while restoring project + images from URL; prevents the upload view flashing empty
  if (isRehydrating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Check if we're in a project workflow (staging or floorplan)
  const isInStagingWorkflow = currentProject && ['upload', 'review', 'upscale', 'branding', 'floorplan-upload', 'floorplan-unit-selection', 'floorplan-labels', 'floorplan-editor', 'floorplan-review', 'floorplan-3d', 'floorplan-branding'].includes(currentView);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Onboarding dialog — shown as overlay for new users */}
      {!isOnboarded && <OnboardingView onComplete={handleOnboardingComplete} />}

      {/* Lightbox */}
      {lightboxUrl && (
        <Lightbox 
          url={lightboxUrl} 
          onClose={() => setLightboxUrl(null)}
          onDownload={() => handleDownloadSingle(lightboxUrl, 'estage_Image.png')}
        />
      )}

      {/* Regeneration Modal */}
      {activeRegen && (
        // Wrapper kept mounted (modal state — feedback etc. — survives) but
        // hidden while the generation runs in the background, so re-showing it
        // for the advisory/error still has the user's typed feedback.
        <div className={bgGenerating ? 'hidden' : undefined}>
        <RegenerationModal
          key={`${activeRegen.imgId}_${activeRegen.type}`}
          onClose={() => { if (!isRegenerating) { setActiveRegen(null); setRegenError(null); setRegenSuggestion(null); setBgGenerating(false); } }}
          onConfirm={async (
            feedback: string | null,
            isRefinement: boolean,
            boxCoords?: BoxCoordinates,
            override?: { finalPrompt: string; scope: 'global' | 'local'; intent: 'cosmetic' | 'structural' },
            forceEdit?: boolean,
          ) => {
            setRegenError(null);
            setRegenSuggestion(null);
            const storagePath = activeRegen.currentStoragePath;
            const targetImgId = activeRegen.imgId;
            const targetType = activeRegen.type;
            // True while the modal (if any) is still open for THIS tile — used by
            // the async completion below so a background generation for tile A
            // never touches the modal the user has since opened for tile B.
            const stillMine = () => {
              const a = activeRegenRef.current;
              return !!a && a.imgId === targetImgId && a.type === targetType;
            };

            // Capture dbImageId now, before any awaits, from current results state
            const dbImageId = results.find(
              (r) =>
                (r.originalImageId === targetImgId || r.dbImageId === targetImgId) &&
                r.type === targetType,
            )?.dbImageId;

            // Variant-robust refinement needs the parent images.id (dbImageId);
            // without it, fall back to the generate-stage regeneration path.
            const useRefinementPath =
              isRefinement &&
              !!feedback &&
              !!storagePath &&
              !!dbImageId &&
              (isAdmin ? appSettings.stage_refinement_admin : appSettings.stage_refinement_live);

            setIsRegenerating(true);
            // Hide the modal for the duration — the tile's own loading overlay
            // takes over so the screen stays interactive (like the re-roll path).
            setBgGenerating(true);

            // Immediately mark the result tile as loading so the overlay appears
            setResults((prev) =>
              prev.map((r) =>
                (r.originalImageId === targetImgId || r.dbImageId === targetImgId) && r.type === targetType
                  ? { ...r, isLoading: true, error: undefined }
                  : r
              )
            );

            try {
              if (useRefinementPath && feedback && storagePath) {
                const result = await requestStageRefinement({
                  storagePath,
                  imageDbId: dbImageId!,
                  versionType: targetType === GenerationType.EMPTY ? 'Empty' : 'Staged',
                  userPrompt: feedback,
                  boxCoords,
                  promptOverride: override?.finalPrompt,
                  scopeOverride: override?.scope,
                  intentOverride: override?.intent,
                  forceEdit,
                });
                // Advisory: the classifier suggests a full regeneration (e.g. a
                // perspective change) — no image was generated, no credit charged.
                // Show the advisory in the modal; leave the tile as-is.
                if (result.regenerate_suggested) {
                  const reason = result.regenerate_reason ?? null;
                  setIsRegenerating(false); // clear before showing the modal, no spinner flash
                  if (stillMine()) {
                    // Re-open the (hidden) modal with the advisory + its actions.
                    setRegenSuggestion({ reason });
                    setBgGenerating(false);
                    setResults((prev) =>
                      prev.map((r) =>
                        (r.originalImageId === targetImgId || r.dbImageId === targetImgId) && r.type === targetType
                          ? { ...r, isLoading: false }
                          : r
                      )
                    );
                  } else {
                    // User moved on to another tile — surface the advice on this
                    // tile instead (its "Erneut versuchen" re-rolls = neu generieren).
                    setResults((prev) =>
                      prev.map((r) =>
                        (r.originalImageId === targetImgId || r.dbImageId === targetImgId) && r.type === targetType
                          ? { ...r, isLoading: false, error: reason ?? 'Für diese Änderung besser neu generieren.' }
                          : r
                      )
                    );
                  }
                  return;
                }
                const newPublicUrl = supabase.storage.from('images').getPublicUrl(result.storage_path!).data.publicUrl;
                setResults((prev) =>
                  prev.map((r) => {
                    const matches =
                      (r.originalImageId === targetImgId || r.dbImageId === targetImgId) &&
                      r.type === targetType;
                    if (!matches) return r;
                    const previousVersion: GeneratedVersion | null =
                      r.currentUrl && r.storagePath
                        ? { id: `prev_${Date.now() - 1}`, url: r.currentUrl, timestamp: Date.now() - 1, storagePath: r.storagePath }
                        : null;
                    const newVariant: GenerationVariant = {
                      id: `variant_${Date.now()}_refined`,
                      url: newPublicUrl,
                      storagePath: result.storage_path!,
                    };
                    const existingVariants = r.variants || [];
                    return {
                      ...r,
                      isLoading: false,
                      currentUrl: newPublicUrl,
                      storagePath: result.storage_path,
                      variants: [...existingVariants, newVariant],
                      selectedVariantId: newVariant.id,
                      history: previousVersion ? [previousVersion, ...r.history] : r.history,
                    };
                  })
                );
                // Persist the refined image to the images table so it survives navigation.
                // generate-stage-refinement only writes image_history; this keeps images table in sync.
                if (dbImageId) {
                  const updateField = targetType === GenerationType.EMPTY ? 'storage_path_empty' : 'storage_path_staged';
                  supabase
                    .from('images')
                    .update({ [updateField]: result.storage_path })
                    .eq('id', dbImageId)
                    .then(() => {}, (err) => console.warn('[Refinement] images table update failed', err));
                }
                if (user?.id) {
                  refreshCredits();
                }
              } else {
                // Standard re-roll or text-only refinement via generate-stage
                const sourceImagePath = isRefinement && storagePath ? storagePath : undefined;
                await handleRegenerate(targetImgId, targetType, feedback, sourceImagePath);
              }
              // Success — dismiss the (hidden) modal, but only if it's still this
              // tile's (a concurrent edit may have opened the modal for another).
              if (stillMine()) {
                setActiveRegen(null);
                setRegenError(null);
                setBgGenerating(false);
              }
            } catch (err) {
              const error = err as Error;
              console.error('[Regen] Error:', err);
              const msg = error.message || 'Regenerierung fehlgeschlagen.';
              setIsRegenerating(false); // clear before re-showing the modal, no spinner flash
              if (stillMine()) {
                // Re-show the modal with the error so the user can retry without re-typing.
                setBgGenerating(false);
                setRegenError(msg);
                setResults((prev) =>
                  prev.map((r) =>
                    (r.originalImageId === targetImgId || r.dbImageId === targetImgId) && r.type === targetType
                      ? { ...r, isLoading: false }
                      : r
                  )
                );
              } else {
                // User moved on — surface the error on the tile (with its retry).
                setResults((prev) =>
                  prev.map((r) =>
                    (r.originalImageId === targetImgId || r.dbImageId === targetImgId) && r.type === targetType
                      ? { ...r, isLoading: false, error: msg }
                      : r
                  )
                );
              }
            } finally {
              setIsRegenerating(false);
            }
          }}
          onAnalyze={async (feedbackText, boxCoords, opts) => {
            const dbId = results.find(
              (r) => (r.originalImageId === activeRegen.imgId || r.dbImageId === activeRegen.imgId) && r.type === activeRegen.type,
            )?.dbImageId;
            if (!activeRegen.currentStoragePath || !dbId) {
              throw new Error('Analyse nicht möglich — Bilddaten unvollständig.');
            }
            return requestStageRefinementPreview({
              storagePath: activeRegen.currentStoragePath,
              imageDbId: dbId,
              versionType: activeRegen.type === GenerationType.EMPTY ? 'Empty' : 'Staged',
              userPrompt: feedbackText,
              boxCoords,
              classifierPromptOverride: opts?.classifierPromptOverride,
              rewritePromptOverride: opts?.rewritePromptOverride,
              rewritePromptOverrideKey: opts?.rewritePromptOverrideKey,
              assemblyTemplateOverride: opts?.assemblyTemplateOverride,
              assemblyTemplateOverrideKey: opts?.assemblyTemplateOverrideKey,
            });
          }}
          isLoading={isRegenerating}
          error={regenError}
          regenerateSuggestion={regenSuggestion}
          hasCurrentImage={!!activeRegen.currentStoragePath}
          currentImageUrl={activeRegen.currentUrl}
          initialMode={activeRegen.initialMode ?? 'choose'}
        />
        </div>
      )}

      {/* Sidebar */}
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen}>
        <SidebarBody className="justify-between gap-10">
          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
            {/* Logo */}
            <SidebarLogo onClick={() => navigateTo('dashboard')} />

            {/* Back to Project Details (when in staging workflow) */}
            {isInStagingWorkflow && (
              <div className="mt-4">
                <SidebarLinkItem
                  link={{
                    label: 'Zurück zum Projekt',
                    icon: <ArrowLeft className="h-5 w-5 flex-shrink-0" />,
                    onClick: goBackToProjectDetails,
                  }}
                  className="text-muted-foreground hover:text-foreground"
                />
              </div>
            )}

            {/* Navigation Links */}
            <div className="mt-8 flex flex-col gap-2">
              {visibleSidebarLinks.map((link, idx) => {
                const childLinks = (link.children ?? []).filter(
                  (c) => isAdmin || !c.featureKey || !disabledFeatures.includes(c.featureKey)
                );
                return (
                  <div key={idx} className="flex flex-col gap-1">
                    <SidebarLinkItem link={link} />
                    {childLinks.map((child, cidx) => (
                      <SidebarLinkItem
                        key={cidx}
                        link={child}
                        indented
                        className="py-1.5 text-[13px] text-white/60 hover:text-white"
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* User & Logout */}
          <div className="flex flex-col gap-2">
            <SidebarLinkItem
              link={{
                label: 'Abmelden',
                icon: <LogOut className="h-5 w-5 flex-shrink-0" />,
                onClick: handleSignOut,
              }}
            />
            <SidebarUserItem email={user.email || ''} avatarUrl={userAvatarUrl} />
          </div>
        </SidebarBody>
      </Sidebar>

      {/* Main Content */}
      <main ref={mainRef} className="flex-1 overflow-y-auto">
        {/* TopBar (Guthaben, Profil …) auch im Flow-v3-Assistenten sichtbar.
            Der Assistent bleibt Vollbild (kein Content-Padding); sein eigener
            Sticky-Header klebt unter der TopBar (top-[60px]), damit sich beide
            nicht überlagern. */}
        <TopBar
          title={VIEW_META[currentView]?.title ?? null}
          subtitle={VIEW_META[currentView]?.subtitle ?? null}
          credits={credits}
          floorplanUnits={floorplanUnits}
          isLoadingCredits={isLoadingCredits}
          unreadCount={computeUnreadCount(updates, readUpdateIds)}
          email={user.email || ''}
          avatarUrl={userAvatarUrl}
          disabledFeatures={disabledFeatures}
          isAdmin={isAdmin}
          hasTeam={hasTeam}
          plan={creditState?.plan ?? null}
          subscriptionStatus={creditState?.subscriptionStatus ?? null}
          onNavigate={navigateTo}
          onNavigateSettings={navigateToSettings}
          onOpenWhatsNew={openWhatsNewManual}
          onSignOut={handleSignOut}
        />
        <div className={currentView === 'wizard' ? 'w-full' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
          {/* Dashboard */}
          {currentView === 'dashboard' && (
            <DashboardView
              projects={projects}
              isLoading={isLoadingProjects}
              onCreateNew={startNewStaging}
              onLoadProject={openProjectDetails}
              onDeleteProject={handleDeleteProject}
              creatorMap={creatorMap}
              currentUserId={user?.id}
              isAdmin={isAdmin}
              teamMembers={teamMembersList}
              projectAssignments={projectAssignments}
            />
          )}

          {/* Flow v3: Projekt-Assistent (Vollbild-Wizard) */}
          {currentView === 'wizard' && (
            <ProjectWizardView
              mode={wizardMode}
              project={currentProject}
              isAdmin={isAdmin}
              credits={credits}
              onCreditsRefresh={refreshCredits}
              onProjectCreated={(p) => {
                setCurrentProject(p);
                setProjects((prev) => [p, ...prev.filter((x) => x.id !== p.id)]);
              }}
              onExit={(dest) => {
                setWizardBusy(false);
                if (dest === 'workspace' && currentProject) {
                  setCurrentView('workspace');
                } else {
                  navigateTo('dashboard');
                }
              }}
              onBusyChange={setWizardBusy}
            />
          )}

          {/* Flow v3: Projekt-Workspace (Tabs: Bilder / Grundriss / Export) */}
          {currentView === 'workspace' && currentProject && (
            <ProjectWorkspaceView
              project={currentProject}
              isAdmin={isAdmin}
              onCreditsRefresh={refreshCredits}
              onAddImages={() => {
                setWizardMode('add');
                setCurrentView('wizard');
              }}
              onOpenFloorplan={openFloorplanForProject}
              onOpenFloorplanBranding={openFloorplanBrandingForProject}
              onBack={() => navigateTo('dashboard')}
              onProjectUpdated={handleProjectUpdate}
            />
          )}

          {/* Project Details (Hub) */}
          {currentView === 'project-details' && currentProject && (
            <ProjectDetailsView
              project={currentProject}
              onBack={() => navigateTo('dashboard')}
              onOpenStaging={openStagingForProject}
              onOpenStagingWithSelection={openStagingForProjectWithSelection}
              onOpenOverlay={openOverlayForProject}
              onOpenFloorplan={openFloorplanForProject}
              onImageClick={setLightboxUrl}
              onProjectUpdate={handleProjectUpdate}
              onHeroSave={handleHeroSave}
              isAdmin={isAdmin}
              teamMembers={teamMembersList}
            />
          )}

          {/* Setup (New Project) */}
          {currentView === 'setup' && !flowV3Active && (
            <SetupView
              name={newProjectName}
              address={newProjectAddr}
              isEditing={false}
              onNameChange={setNewProjectName}
              onAddressChange={setNewProjectAddr}
              onCancel={() => navigateTo('dashboard')}
              onCreate={handleCreateProject}
            />
          )}

          {/* Upload */}
          {currentView === 'upload' && (
            <UploadView
              uploadedImages={uploadedImages}
              projectName={currentProject?.name}
              onImageUpload={handleImageUpload}
              onFilesDrop={handleFilesDrop}
              onUpdateImageMeta={updateImageMeta}
              onDeleteImage={handleDeleteImage}
              onStartGeneration={startGeneration}
              onImageClick={setLightboxUrl}
              credits={credits}
              onBuyCredits={() => navigateTo('credits')}
              stagingStyle={currentProject?.stagingStyle}
              onStagingStyleChange={handleStagingStyleChange}
              stagingV2Active={catalogStagingActive}
            />
          )}

          {/* Review */}
          {currentView === 'review' && (
            <ReviewView
              uploadedImages={uploadedImages}
              results={results}
              onRegenerate={(imgId, type, _storagePath, emptyConsultOverride) => {
                // Direct re-roll — no modal needed, tile overlay shows progress
                handleRegenerate(imgId, type, null, undefined, emptyConsultOverride);
              }}
              onEdit={(imgId, type, currentStoragePath) => {
                // Open modal in feedback mode directly. Reset bgGenerating so a
                // still-running background edit on another tile can't keep this
                // freshly-opened modal hidden.
                const res = results.find(r => r.originalImageId === imgId && r.type === type);
                setBgGenerating(false);
                setActiveRegen({ imgId, type, currentStoragePath, currentUrl: res?.currentUrl ?? undefined, initialMode: 'feedback' });
              }}
              onVariantSelect={handleVariantSelect}
              onStageApproved={handleStageApproved}
              onCatalogAnalyze={handleCatalogAnalyze}
              stagingStyle={currentProject?.stagingStyle}
              onStagingStyleChange={handleStagingStyleChange}
              catalogStagingEnabled={catalogStagingActive}
              onNext={() => { trackVariantSelections(); setCurrentView('upscale'); }}
              onBack={goBackToUpload}
              onImageClick={setLightboxUrl}
              onDownloadSingle={handleDownloadSingle}
              isAdmin={isAdmin}
              disabledFeatures={disabledFeatures}
            />
          )}

          {/* Upscale Selection */}
          {currentView === 'upscale' && (
            <UpscaleSelectionView
              uploadedImages={uploadedImages}
              results={results}
              credits={credits}
              upscaleResults={upscaleResults}
              isUpscaling={isUpscaling}
              onUpscaleSelected={handleUpscaleSelected}
              onSkip={() => setCurrentView('branding')}
              onBack={goBackToReview}
              onBuyCredits={() => navigateTo('credits')}
              onNext={() => setCurrentView('branding')}
            />
          )}

          {/* Branding */}
          {currentView === 'branding' && (
            <BrandingView
              branding={branding}
              results={results}
              isProcessing={isProcessingBranding}
              onBrandingUpload={handleBrandingUpload}
              onBrandingUpdate={updateBranding}
              onDownloadAll={handleDownloadAll}
              aiLabel={aiLabel}
              onBack={goBackToUpscale}
              onSaveAndClose={currentProject ? handleSaveAndClose : undefined}
              projectAspectRatio={currentProject?.aspectRatio}
              defaultAssetIds={defaultBrandingAssetIds}
              project={currentProject ?? undefined}
              onHeroSave={currentProject ? handleHeroSave : undefined}
            />
          )}

          {/* Overlay Tool */}
          {currentView === 'overlay-tool' && (
            <OverlayToolView
              projectId={currentProject?.id}
              projectName={currentProject?.name}
              onBack={currentProject ? goBackToProjectDetails : undefined}
            />
          )}

          {/* Overlay Builder */}
          {currentView === 'overlay-builder' && (
            <OverlayBuilderView
              onSave={() => {
                // Rücksprung-Ziel aus ?return= (z. B. Wizard/Workspace-Export) —
                // nur app-interne Pfade zulassen.
                const returnTo = new URLSearchParams(window.location.search).get('return');
                if (returnTo && returnTo.startsWith('/app/')) {
                  window.location.assign(returnTo);
                } else {
                  navigateTo('overlay-tool');
                }
              }}
            />
          )}

          {/* Settings */}
          {currentView === 'settings' && user && (
            <SettingsView userId={user.id} credits={credits} initialTab={settingsInitialTab} />
          )}

          {/* Credits */}
          {currentView === 'credits' && (
            <CreditsView
              credits={credits}
              creditState={creditState}
              onCreditStateRefresh={refreshCredits}
            />
          )}

          {/* Affiliate Dashboard */}
          {currentView === 'affiliate' && (
            <AffiliateDashboardView
              onBack={() => navigateTo('dashboard')}
              onNavigateToPayoutSettings={() => navigateToSettings('affiliate')}
            />
          )}

          {/* Hilfe & Tutorials */}
          {currentView === 'help' && (
            <HelpView tutorials={tutorials} onVideoOpened={handleTutorialViewed} />
          )}

          {/* Floorplan Upload */}
          {currentView === 'floorplan-upload' && currentProject && (
            <FloorplanUploadView
              projectId={currentProject.id}
              floorplanState={floorplanState}
              onFloorplanStateUpdate={updateFloorplanState}
              onNext={() => setCurrentView('floorplan-unit-selection')}
              onBack={goBackToProjectDetails}
              credits={credits}
              floorplanUnits={floorplanUnits}
              onBuyCredits={() => navigateTo('credits')}
              onContinueExisting={handleContinueExistingFloorplan}
              onStartOver={handleStartOverFloorplan}
            />
          )}

          {/* Floorplan Unit Selection */}
          {currentView === 'floorplan-unit-selection' && currentProject && (
            <FloorplanUnitSelectionView
              projectId={currentProject.id}
              floorplanState={floorplanState}
              onFloorplanStateUpdate={updateFloorplanState}
              onExtractUnit={handleUnitExtraction}
              onContinue={() => setCurrentView('floorplan-labels')}
              onBack={() => setCurrentView('floorplan-upload')}
              credits={credits}
              floorplanUnits={floorplanUnits}
            />
          )}

          {/* Floorplan Labels (Room Designations) */}
          {currentView === 'floorplan-labels' && currentProject && (
            <FloorplanLabelsView
              floorplanState={floorplanState}
              onFloorplanStateUpdate={updateFloorplanState}
              onNext={() => setCurrentView('floorplan-editor')}
              onBack={() => setCurrentView('floorplan-unit-selection')}
              credits={credits}
            />
          )}

          {/* Floorplan Editor (Fixtures + Config) */}
          {currentView === 'floorplan-editor' && currentProject && (
            <FloorplanEditorView
              projectId={currentProject.id}
              floorplanState={floorplanState}
              onFloorplanStateUpdate={updateFloorplanState}
              onGenerate={handleGenerate2D}
              onNavigateToReview={() => setCurrentView('floorplan-review')}
              onBack={() => setCurrentView('floorplan-labels')}
              credits={credits}
              floorplanUnits={floorplanUnits}
              onBuyCredits={() => navigateTo('credits')}
              isAdmin={isAdmin}
              onGenerateWithPrompt={(finalPrompt) => handleGenerate2D(undefined, finalPrompt)}
            />
          )}

          {/* Floorplan Review */}
          {currentView === 'floorplan-review' && currentProject && user && (
            <FloorplanReviewView
              projectId={currentProject.id}
              currentProject={currentProject}
              floorplanState={floorplanState}
              onFloorplanStateUpdate={updateFloorplanState}
              onFreshGeneration2D={handleGenerate2D}
              onNavigateTo3D={() => setCurrentView('floorplan-3d')}
              onBack={() => setCurrentView('floorplan-editor')}
              userId={user.id}
              credits={credits}
              floorplanUnits={floorplanUnits}
            />
          )}

          {/* Floorplan 3D */}
          {currentView === 'floorplan-3d' && currentProject && (
            <FloorplanThreeDView
              floorplanState={floorplanState}
              onGenerate3D={handleGenerate3D}
              onNavigateToBranding={handleNavigateToFloorplanBranding}
              onDownload={handleDownloadSingle}
              onBack={() => setCurrentView('floorplan-review')}
              credits={credits}
              floorplanUnits={floorplanUnits}
            />
          )}

          {/* Floorplan Branding */}
          {currentView === 'floorplan-branding' && currentProject && (
            <FloorplanBrandingView
              floorplanState={floorplanState}
              currentProject={currentProject}
              onSave2DTransform={handleSave2DTransform}
              onSave3DTransform={handleSave3DTransform}
              onSaveArrow2D={handleSaveArrow2D}
              onSaveArrow3D={handleSaveArrow3D}
              onSaveComposite={handleSaveComposite}
              onBack={() => setCurrentView(floorplanState.generated3dUrl ? 'floorplan-3d' : 'floorplan-review')}
              onSaveAndClose={handleFloorplanSaveAndClose}
            />
          )}

          {/* Admin Dashboard */}
          {currentView === 'admin' && (
            <AdminRoute onAccessDenied={() => navigateTo('dashboard')}>
              <AdminDashboard onBack={() => navigateTo('dashboard')} />
            </AdminRoute>
          )}
        </div>
      </main>

      <WhatsNewModal
        open={whatsNewOpen}
        items={whatsNewItems}
        readIds={readUpdateIds}
        onOpenChange={setWhatsNewOpen}
        onView={handleUpdateRead}
      />

      {/* Signup-Tutorials: öffnet sich einmalig direkt nach dem Onboarding */}
      <TutorialPlayerModal
        videos={signupTutorials}
        open={signupTutorialsOpen}
        onOpenChange={setSignupTutorialsOpen}
        onViewed={handleTutorialViewed}
      />

      {flowV3Active && (
        <WizardResumeBubble
          projects={projects}
          currentView={currentView}
          currentProjectId={currentProject?.id ?? null}
          onResume={resumeWizardFlow}
        />
      )}
      {flowV3Active && (
        <FloorplanResumeBubble
          projects={projects}
          currentView={currentView}
          currentProjectId={currentProject?.id ?? null}
          onResume={(id) => void resumeFloorplanFlow(id)}
        />
      )}
      <NewProjectModal
        open={newProjectModalOpen}
        isCreating={isCreatingNewProject}
        lockedTool={newProjectLockedTool}
        onClose={() => setNewProjectModalOpen(false)}
        onConfirm={handleNewProjectConfirm}
      />
      <HelpButton chatEnabled={isAdmin ? appSettings.support_chat_admin : appSettings.support_chat_live} />
    </div>
  );
};

export default App;
